import type { Creature, Effect, Fixture, GroundItem, Item, ItemDef, LevelTiles, MapReset, Marks, NpcMenu, OpenFloor, OpenSpace, QuestMark, Rect, Seen, State, Steps, Text, Track, Trade, UnlightZone, UnseenLight, Welcome, You } from "./types";

export { sameItem } from "./pile";
import { sameItem } from "./pile";
import { Tile } from "./types";
import { BLOOD_RGB, setTwoHandedItems, SPATTER_RGB } from "./sprites";
import { isBody } from "./corpses";
import { ARROW_MS, type Missile, missileKind, missileMs } from "./missiles";
import { isRampFoot } from "./ramps";
import { num } from "./numfmt";

/** A creature as the client tracks it, with interpolation state for walking. */
export interface ClientCreature extends Creature {
  /** Tile the creature is walking from, or the same as x/y when idle. */
  fromX: number;
  fromY: number;
  moveStart: number; // performance.now() when the step began
  moveDur: number; // ms for the whole step
  lastDir: number;
  /** Steps heard from the server and not yet begun, earliest first: see
   *  Game.onSteps. */
  queue: QueuedStep[];
  /**
   * Where the creature stands once every step heard has been played, and
   * which way it then faces: the server's word as the wire has it so far,
   * a step or two ahead of what is being drawn. It is what the next state
   * is checked against, rather than x and y, so that a state can never
   * pull back a walker whose steps are still being played.
   */
  wireX: number;
  wireY: number;
  wireZ: number;
  wireDir: string;
}

/** A step heard from the server, waiting for the moment it is drawn from. */
export interface QueuedStep {
  x: number;
  y: number;
  z: number;
  dir: string;
  via?: { x: number; y: number };
  /** performance.now() at which it begins. */
  start: number;
  /** How long it lasts, in ms: the server's own figure. */
  dur: number;
}

/**
 * How long after somebody else's step began, by the server's clock mapped
 * onto this page's, it starts being drawn. Asked for on 2026-09-28: "the
 * client keeps an offset between server time and its own clock ... and
 * plays each step starting at (server time + offset + playout delay), with
 * a playout delay around 60–100 ms (a constant, named and commented),
 * animating over exactly the move ms".
 *
 * It is what buys smooth walking. The offset is the quickest a message
 * has come across lately, so every message slower than that — which is
 * most of them, by a few milliseconds, and some by tens — would otherwise
 * arrive after its step should have begun, and the walker would stand at
 * the end of one tile waiting for word of the next. Eighty covers the
 * jitter of an ordinary connection with room to spare, and is short
 * enough that nobody reads the world as lagging: a monster is drawn a
 * tile's fraction later than it is, never a tile behind.
 *
 * Your own character does not wait for it: see onSteps.
 */
export const PLAYOUT_DELAY_MS = 80;

/**
 * How long a reading of the clock is remembered. The offset between the
 * server's clock and this page's is the least one-way lag seen in this
 * long — the least, because a message can be held up but never arrive
 * early, so the quickest one is the one nearest the truth. A window rather
 * than for ever, so a connection that has got slower for good (or two
 * clocks drifting apart) is followed within this many seconds.
 */
export const CLOCK_WINDOW_MS = 10_000;

/**
 * The tiles nothing walks on, which is the server's own list (see
 * Tile.Walkable there). The client only needs it to tell ground from sea,
 * and the one thing it tells it for is which tiles a country covers.
 */
const BLOCKING_TILES = new Set<number>([
  Tile.Water, Tile.Wall, Tile.Tree, Tile.Rock, Tile.CaveWall, Tile.Palisade,
  Tile.Hedge, Tile.Hillside, Tile.HobbitDoor, Tile.MillWheel, Tile.PartyTree, Tile.Ship,
  Tile.FrozenWaste,
]);

/** How an experience gain reads over your head: the number, then what it is. */
const xpText = (n: number) => `${num(n)} XP`;

/** Experience is drawn small — it is a running tally, not a blow landing. */
const XP_SIZE = 7;

/**
 * How long a healing number gathers what arrives after it, and how long it
 * then lives. One second is the gather because it is the rate healing is
 * quoted at everywhere else — a potion is thirty a second — so the
 * number over your head and the number in the changelog are the same
 * number. The life is twice that so a total has stopped changing by the
 * time it is halfway through fading, which is the same bargain the merged
 * experience number strikes with its own clock.
 */
const HEAL_GATHER_MS = 1000;
const HEAL_TTL = 2000;

/**
 * How far under its usual spot a floating number may start, in world
 * pixels, to clear the ones already rising from the same tile: one row of
 * the usual size, which keeps the newest on the figure it belongs to. See
 * pushFloat.
 */
const FLOAT_MAX_DROP = 13;

export interface FloatingText {
  /**
   * The level it happened on, for the oblique view to lift it to the
   * height it happened at; the viewer's own level when left out.
   */
  z?: number;
  x: number; // tile
  y: number;
  text: string;
  color: string;
  born: number;
  ttl: number;
  /** Extra starting distance below the usual spot, in pixels, so numbers
   * sharing a tile stack under each other instead of overlapping. */
  drop: number;
  /**
   * What this float may be added to, for the kinds that are a running
   * total rather than an event: experience from two kills at once is one
   * number, not two stacked. Floats with no merge key never combine — two
   * hits landing together are two blows, and each is worth seeing.
   */
  merge?: string;
  /**
   * Who was standing on the tile it landed on, for it to be drawn over
   * them rather than over the tile. The server says where a blow landed by
   * the tile the one struck holds, and the figure is drawn up to a step
   * behind that: other creatures' steps play out PLAYOUT_DELAY_MS late and
   * every step slides from one tile to the next. A number pinned to the
   * tile sits off the figure for as long as it is walking, and a player
   * the Balrog's whip drags in lands their number a tile or more ahead of
   * their body (2026-10-20: "sometimes the numbers inflicted on and by
   * other players appears off-centre, balrog in moria in particular?").
   */
  on?: number;
  /** Where it was last drawn, in tiles, for it to stay there once whoever it
   *  followed is gone, rather than jumping back to the tile. */
  ax?: number;
  ay?: number;
  /** The running total behind the text, for merged floats. */
  value?: number;
  /**
   * How big to draw it, in world pixels; the usual size when left out.
   * Experience is set small: it arrives on every kill, so it should be
   * readable without shouting over the damage numbers it lands among.
   */
  size?: number;
}

/**
 * Words hang where they were said, not over whoever said them: a shout from
 * the doorway stays at the doorway. id is only there so a speaker's next line
 * replaces their last.
 *
 * An answer hangs the same way, over the tile you were standing on when you
 * asked, and walking off leaves it behind. Making this one kind follow its
 * creature was the alternative — it is your own head, after all — and it
 * would mean two rules for the same bubble, one of which only your own
 * character obeys. A look and a bearing are both answers about a moment and
 * a place, and the tile they hang over is that place.
 */
export interface SpeechBubble {
  /**
   * The level it happened on, for the oblique view to lift it to the
   * height it happened at; the viewer's own level when left out.
   */
  z?: number;
  id: number;
  x: number; // tile
  y: number;
  text: string;
  /**
   * Who said it. The bubble over a head does not need this — the nameplate
   * is drawn one line under it and would say the same thing twice — but the
   * one pinned to the edge of the screen has no head under it and no
   * nameplate anywhere, and a voice out of the dark that will not say whose
   * it is is half a message. Carried on every bubble rather than looked up
   * from the creature at drawing time because the speaker is precisely the
   * creature the client cannot see: they are off screen, so they are not in
   * `creatures` at all, and a lookup would come back empty in exactly the
   * case the name is wanted.
   */
  from: string;
  born: number;
  ttl: number;
  /**
   * Which of the three things that hang over a head this is: words
   * somebody said, a noise something made — Munch., Chomp. — or the
   * world's answer to a question you asked and nobody else did. One field
   * rather than a flag apiece because they are exclusive: a line is
   * exactly one of the three, and two booleans can say it is both.
   */
  kind: "say" | "noise" | "answer" | "critical";
  /**
   * The words of a spell going off, which a say can be: drawn in
   * SPELL_COLOR rather than a say's yellow (2026-09-24: "show the words for
   * spells in another color").
   */
  spell?: boolean;
  /** A yell: pinned from off screen out to YELL_PIN_RANGE. */
  yell?: boolean;
}

/**
 * The colour spell words are shown in, over the head and in the log: a pale
 * violet, which nothing else in the world is written in. The log's is the
 * same, in style.css under #log .spells.spell.
 */
export const SPELL_COLOR = "#c9a7ff";

export interface Spark {
  /**
   * The level it happened on, for the oblique view to lift it to the
   * height it happened at; the viewer's own level when left out.
   */
  z?: number;
  x: number;
  y: number;
  kind: "death" | "miss" | "block" | "spark" | "blood" | "levelup" | "break" | "fire" | "rift" | "beam" | "splash" | "nova" | "cleanse"
    | "find" | "provoke" | "challenge" | "haste" | "light" | "bond" | "unbond" | "sanctify" | "mend" | "elbereth" | "convince"
    | "shriek" | "webbed" | "lull" | "crown"
    // The Balrog's whip wound round whoever it holds (2026-10-20).
    | "coiled"
    // Sauron's mace going through a tile of its arc, from fx/fy, and
    // whoever it threw lying dazed where they landed (2026-11-01).
    | "sweep" | "knockdown"
    // Longbottom (empty-areas site 1, 2026-10-16): a pipe's smoke ring.
    | "smoke"
    // An admin's /vanish (2026-10-27): a burst of dark smoke and a flash.
    | "vanish"
    // An admin's /summon (2026-10-27): a ring of light drawing in and
    // flaring up where something arrived.
    | "summon"
    // Fast mending (2026-10-17): soft green motes off whoever is healing
    // faster than the ordinary rate in the Party Tree's flowers.
    | "regen";
  born: number;
  /** The spatter item a blood puff takes its colour from; unused otherwise. */
  id?: number;
  /**
   * How far a fire nova reached, in tiles, as the server measured it;
   * unused by every other kind.
   *
   * It is carried rather than assumed because the reach is the server's
   * number and nothing else here is allowed to have an opinion about it.
   * A client that drew two tiles because two tiles is what the spell does
   * today would be a second copy of a constant, and the day the radius
   * moves the picture would go on showing the old one — which is the
   * worst kind of wrong, since what a player reads off a nova is exactly
   * how far it went.
   */
  r?: number;
  /**
   * Which way a find person's needle settles, in eighths of a turn
   * clockwise from north plus one, as the server worked it out; nought, or
   * missing, for no way at all. Only the caster is ever sent a way: every
   * other screen is sent nought, and its needle spins and settles on
   * nothing. Unused by every other kind.
   */
  dir?: number;
  /**
   * The tile a blood bond's thread starts from, the caster's, where x and
   * y are the tile it lands on. Pinned when the message arrives, like an
   * arc's two ends, rather than followed. Missing when the caster is
   * somebody this screen was never sent, and then only the knot at the far
   * end is drawn. Unused by every other kind.
   */
  fx?: number;
  fy?: number;
  /**
   * How long this one lives, in milliseconds, for the one kind whose life
   * the server says rather than the client: a web on a tile, which stays
   * exactly as long as it holds whoever is in it (2026-10-12). Unused by
   * every other kind, which live to sparkMs.
   */
  ms?: number;
}

/**
 * How long a tile of dragon fire burns on screen. Every other spark is a
 * puff that marks a moment; fire marks a shape — which tiles the wave
 * covered — and that shape is the thing a player has to read and step out
 * of before the next breath. Two seconds is long enough to look at twice
 * and well short of the eight or more between waves, so the ground is
 * never permanently alight.
 */
export const FIRE_MS = 2200;

/**
 * How long a rift stays open. Like fire it marks a shape rather than a
 * moment — every tile within three of the demon, which is the ring a player
 * has to be outside of — so it wants long enough to be looked at and stepped
 * out of. Shorter than fire because it goes off far more often and the
 * ground would never be shut otherwise.
 */
export const RIFT_MS = 1500;
/** How long a tile of Sauron's mace sweep is drawn swinging through. */
export const SWEEP_MS = 450;

/**
 * How long the light of an eagle's coming or going (a homecoming's until
 * 2026-10-10) stands on a tile. Longer than a puff
 * and shorter than fire: it is a moment rather than a shape, but it is the
 * only sign anybody else gets that a person left or arrived, so it wants
 * long enough to be looked up at and understood.
 */
export const BEAM_MS = 1200;

/**
 * How long the rings of a cast stay on the water. A puff's seven hundred
 * milliseconds is not enough: a splash is not one flash but a thing that
 * grows, and rings that have not finished widening before they are taken
 * away read as a blink rather than as water moving. It is also the one
 * spark that happens on a clock the player sets — a cast every 1.5 s — so
 * it wants to be well clear of the next one, or an angler working a lake
 * leaves a permanent disturbance instead of a series of casts.
 */
export const SPLASH_MS = 950;

/**
 * How long the drops of an antidote take to rise off the caster and go.
 * A little over the puff's seven hundred, because this is not a moment
 * but a short journey: each drop has to be seen leaving the body, rising
 * clear of the head and paling out, and the ones that set off last need
 * the tail of the clock to get there. Well short of a second, though, so
 * it reads as the spell going off and not as a state the caster is in.
 */
export const CLEANSE_MS = 800;
/** How long a heal's rising crosses take: the antidote's drops' length. */
export const MEND_MS = 800;
/**
 * How long a sanctification's light takes (asked for on 2026-09-27: "when
 * sanctification is cast, do a spell effect for it"): long enough for the
 * shaft to come down, the ring to spread and the motes to rise off it.
 */
export const SANCTIFY_MS = 1400;
/** Elbereth going off (2026-10-21): a star over the caster, and its light going out across the floor. */
export const ELBERETH_MS = 1100;
/**
 * How long Durin's Crown shines in the Mirrormere for whoever looks into it
 * (2026-10-16, Azanulbizar): the seven stars come up one by one out of the
 * deep water, hold, and sink again, slow enough to be read as a crown.
 */
export const CROWN_MS = 4000;

/**
 * How long a fire nova burns.
 *
 * It marks a shape, the way fire and a rift do — the square it covered,
 * which is the one thing a player has to be able to read off it — so it
 * wants longer than a puff: the front has two tiles to travel and the eye
 * has to be able to follow it out and see where it stopped. But it is
 * nothing like fire's 2200, because a nova leaves NO GROUND TO STEP OUT
 * OF. A dragon's wave and a demon's rift burn for as long as they do
 * because what they are telling you is where not to be standing when the
 * next one comes; a nova is over in the instant it goes off, there is no
 * next one for a hundred and thirty-five seconds, and fire that lay on the
 * floor afterwards would be saying that the tiles are still dangerous when
 * they are not.
 *
 * Nine hundred, then, which is the splash's clock almost exactly, and
 * deliberately: a splash is the other thing in this world that spreads
 * outwards from one tile and has to finish spreading before it is taken
 * away, or it reads as a flash rather than as something happening. The
 * two are the same drawing problem in two colours.
 */
export const NOVA_MS = 900;

/**
 * How long the Witch-king's shriek is drawn spreading out of him
 * (2026-10-12): rings riding out to the edge of what it caught, a little
 * slower than a nova, since a cry carries rather than bursts.
 */
export const SHRIEK_MS = 1100;

/**
 * How long Old Man Willow's song is drawn drifting out of him
 * (2026-10-12): slow, soft rings, slower than the shriek, since it is a
 * lullaby and not a cry.
 */
export const LULL_MS = 1800;

/**
 * How long a pipe's smoke ring takes to rise and fade (Longbottom,
 * 2026-10-16; see longbottom.go): slow, as smoke is.
 */
export const SMOKE_MS = 2200;

/**
 * How long an admin's /vanish takes to burst and clear (2026-10-27: "it
 * lets an admin go invisible with a fancy flair"; see vanish.go).
 */
export const VANISH_MS = 1400;

/**
 * How long an admin's /summon takes to draw in and flare (2026-10-27:
 * "when summoning using /summon, there should be a visual effect").
 */
export const SUMMON_MS = 1300;

/**
 * How long a beat of fast mending's motes take to rise and fade
 * (2026-10-17: "show fast healing from sources like the party tree in the
 * game view"): slow and soft, since it comes every beat and is a state the
 * player is in rather than a spell going off.
 */
export const REGEN_MS = 1600;

/**
 * How long Shelob's web takes to fly to whoever it was thrown at.
 */
export const WEB_FLIGHT_MS = 320;

/**
 * How much of a nova's life the fire spends travelling outwards, as a
 * share of NOVA_MS. The rest of it the finished square stands still and
 * burns down.
 *
 * A third, and the number was settled by watching one go off rather than
 * by argument. At over half — which is what it was first written at, on
 * the reasoning that a thing spreading should spend most of its time
 * spreading — the square only became complete at the moment it had
 * already half faded, so the one thing a player has to read off the
 * picture, which is HOW FAR IT WENT, was never on screen at full
 * strength. Out early and burning down is the right way round: the shape
 * arrives while it is still bright and then has two thirds of the clock
 * to be looked at.
 */
export const NOVA_OUT = 0.35;

/**
 * How long each of the spells that had nothing to show until 2026-09-24
 * shows for, asked for that day: "make sure all spells have a graphical
 * effect when cast". Every one is a moment and not a state (the spell going
 * off, on the tile it went off on, and gone) so every one is short: the
 * puff's seven hundred where the drawing is a single beat, and up to the
 * beam's twelve hundred where something has to be seen travelling or
 * settling before it goes. None of them follows the caster about.
 */
export const FIND_MS = 1100;
export const PROVOKE_MS = 750;
export const HASTE_MS = 750;
export const LIGHT_MS = 1200;
export const BOND_MS = 1000;
export const UNBOND_MS = 700;

/**
 * How long a spark of each kind lives. Most of them are a puff marking the
 * instant something happened and are gone in the time it takes to look up;
 * the ones that cover ground instead of a moment, and the one that has to
 * spread before it dies, burn to their own clocks.
 */
export function sparkMs(s: Pick<Spark, "kind" | "ms">): number {
  const kind = s.kind;
  if (s.ms) return s.ms;
  return kind === "fire" ? FIRE_MS
    : kind === "rift" ? RIFT_MS
    : kind === "beam" ? BEAM_MS
    : kind === "splash" ? SPLASH_MS
    : kind === "cleanse" ? CLEANSE_MS
    : kind === "mend" ? MEND_MS
    : kind === "nova" ? NOVA_MS
    : kind === "find" ? FIND_MS
    : kind === "provoke" || kind === "challenge" ? PROVOKE_MS
    : kind === "haste" ? HASTE_MS
    : kind === "light" ? LIGHT_MS
    : kind === "bond" ? BOND_MS
    : kind === "unbond" ? UNBOND_MS
    : kind === "sanctify" ? SANCTIFY_MS
    : kind === "elbereth" ? ELBERETH_MS
    : kind === "convince" ? ELBERETH_MS
    : kind === "crown" ? CROWN_MS
    : kind === "shriek" ? SHRIEK_MS
    : kind === "lull" ? LULL_MS
    : kind === "smoke" ? SMOKE_MS
    : kind === "vanish" ? VANISH_MS
    : kind === "summon" ? SUMMON_MS
    : kind === "regen" ? REGEN_MS
    : kind === "sweep" ? SWEEP_MS
    : 700;
}

/**
 * Something in flight, from whoever sent it to the tile it lands on: an
 * archer's arrow, or the lava a dwarf geomancer hurls. They travel the
 * same way and are drawn differently, which is the whole of the difference
 * — one is a shaft with a head on it and one a lump of molten rock.
 *
 * A demon's bolt was the third of these until 2026-09-21, when it became a
 * beam that covered a line of tiles, sent a tile at a time like a dragon's
 * wave. The beam went on 2026-10-12, when the Balrog was given its whip
 * instead, and the whip is a shot: one lash from the Balrog out to the
 * one person it catches.
 */
export interface Shot {
  /**
   * The level it happened on, for the oblique view to lift it to the
   * height it happened at; the viewer's own level when left out.
   */
  z?: number;
  from: number; // creature id of whoever sent it
  x: number; // target tile
  y: number;
  born: number;
  /**
   * How long it is drawn for, when that is the server's to say rather
   * than its kind's: a Balrog's whip, which holds whoever it catches for
   * as long as the server said before it drags them in (2026-10-20).
   */
  ms?: number;
  /**
   * What is in flight: what a weapon loosed — an arrow, a crossbow bolt or
   * a thrown rock, drawn as what the server said was loosed (see
   * missiles.ts, 2026-09-27) — or a bolt of the dwarf geomancer's lava, or
   * a siren's song, or a Balrog's whip, or a corpse candle's lure. They
   * cross the same tiles; what
   * differs is what is drawn crossing them, and a player should tell at a
   * glance which is coming, since armour answers the first three and a
   * pillar the lava.
   */
  kind: Missile | "lava" | "song" | "web" | "whip" | "lure";
}

/** How long an arrow takes to cross to its target, whatever the distance. */
export const SHOT_MS = ARROW_MS;

/**
 * One link of a chain lightning, frozen at both ends (asked for on 2026-09-21: "Add
 * a chain lightning spell that deals damage that jumps between nearby
 * monsters").
 *
 * It is not a Shot, and the difference is the whole reason it has a type of
 * its own. A Shot keeps the id of whoever loosed it and looks that creature
 * up every frame, so an arrow leaves the archer's hand even as the archer
 * walks. A link of a chain leaps out of a monster the same bolt is often
 * about to kill — that is what the spell is *for* — and a monster that dies
 * is swept out of `creatures` within the second. Following the id would
 * mean the middle of the chain silently not being drawn on exactly the
 * casts that worked best, which is the one picture the spell cannot afford
 * to lose: what a player has to read off it is what it jumped *from* and
 * *to*.
 *
 * So both ends are tile coordinates, resolved once when the message
 * arrives and never looked up again. The server sends every arc of a chain
 * before it takes a single hitpoint off anybody, so at the moment of
 * resolution every creature in the chain is still standing.
 */
export interface Arc {
  /**
   * The level it happened on, for the oblique view to lift it to the
   * height it happened at; the viewer's own level when left out.
   */
  z?: number;
  fx: number; // tile it leapt from
  fy: number;
  x: number; // tile it leapt to
  y: number;
  born: number;
  /**
   * A Istari light's shaft rather than a link of lightning: drawn straight
   * and white, for SHAFT_MS, from the caster to the far end.
   */
  shaft?: boolean;
}

/**
 * How long a Istari light's shaft is drawn for: a little longer than a
 * chain's link, since it is one stroke the eye follows out along the
 * line it was thrown.
 */
export const SHAFT_MS = 750;

/**
 * How long a link of a chain lightning is drawn for.
 *
 * Longer than an arrow's flight and shorter than a tile of demon beam, and
 * both ends of that are argued. An arrow is 220 ms because the eye only has
 * to follow one thing to one place; a chain is three segments that arrive
 * in the same instant and has to be read as a shape — this one, then that
 * one, then that one — and 220 ms is not enough time to look along it. It
 * is well short of the beam's 1400 all the same, because unlike a beam or a
 * wave this marks no ground: there is nothing to step out of afterwards,
 * the thing it says is over the moment it is said, and lightning that hung
 * about would stop reading as lightning.
 */
export const ARC_MS = 600;

/**
 * And how long a siren's song takes to reach whoever it was aimed at.
 * The slower of the two things in flight, and on purpose: the thing worth
 * learning from a song is which patch of water it came out of, because
 * that is what you have to decide whether to walk away from. It is also
 * the one that carries no weapon behind it — nobody loosed it — so the
 * eye has nothing else to follow it back by.
 */
export const SONG_MS = 480;

/**
 * How long a Balrog's whip is drawn for: the lash going out and cracking.
 * Quicker than a song and slower than an arrow, so the eye can follow it
 * back to the Balrog that threw it.
 */
export const WHIP_MS = 380;

/** How long a Balrog's whip takes to pull back to it at the end of its
 *  hold, before the server drags in whoever it held. */
export const WHIP_RETRACT_MS = 300;

/**
 * How long a corpse candle's lure is drawn for (2026-10-15: "Put spirit
 * undead in the dead makes marshes"): a thread of its pale light reaching
 * out to whoever it drew in, slower than the whip, since nothing threw it.
 */
export const LURE_MS = 700;

/** How long a thing in flight is drawn for, by what it is. */
export function shotMs(kind: Shot["kind"]): number {
  // What a weapon loosed flies at its own pace — a bolt quicker than an
  // arrow, a rock slower — and the lava at an arrow's (see missileMs).
  return kind === "song" ? SONG_MS : kind === "whip" ? WHIP_MS : kind === "lure" ? LURE_MS : kind === "lava" ? SHOT_MS : kind === "web" ? WEB_FLIGHT_MS : missileMs(kind);
}

/** How long a fish takes to slide from one tile to the next. Shorter than
 *  the gap between its moves, so it swims a little and then drifts. */
export const FISH_SWIM_MS = 700;

/**
 * What a step through a passage costs, counted in ordinary steps, for the
 * rare update that arrives without the server's own figure on it.
 *
 * It used to be how the climb's length was worked out, alongside the two
 * and a half for a diagonal — and the ground underfoot was missing from
 * that sum entirely, so anything crossing a swamp snapped over at walking
 * pace and then stood still for the rest of its step. The server sends the
 * number now (`move` on a creature), which is the one it is actually
 * waiting out. Working it out in two places had the usual end: this said
 * three and the server said two, and had done for a while.
 */
export const LEVEL_CHANGE_STEPS = 2;

export const DIR_INDEX: Record<string, number> = { n: 0, e: 1, s: 2, w: 3, ne: 1, se: 1, sw: 3, nw: 3 };

/**
 * Where the tile at (x, y) sits in a level's run of tiles, or -1 for a
 * coordinate that is not on the map at all.
 *
 * Every level is kept as one flat run, row after row, so a tile's place in
 * it is y * mapW + x and nothing else. That sum only means what it says
 * inside the map. One column past the east edge is the same number as
 * column nought of the row below; one column before the west edge is the
 * last column of the row above. Nothing in the arithmetic complains, and
 * that is the trouble: an off-map lookup does not come back empty, it
 * comes back holding somebody else's tile, from a row away.
 *
 * Reported on 2026-09-22: "zooming out at ground level is showing roof/hill
 * levels further away on the map like there's a wrapping issue or
 * something". It was a wrap, exactly. The window draws every tile of the
 * viewport, and zooming out is what makes the viewport wider than the
 * world — at eight pixels a tile a wide window is about a hundred and
 * forty tiles across against a world a hundred and twenty-four wide — so
 * the columns hanging off the east edge asked the roof level for tiles the
 * map does not have and were handed the roofs of the row below. A rooftop
 * appeared out in the open sea, a full map width east of its building and
 * one row north of it, and the ground under it was honest water, which is
 * what made it look like the world had been torn and wrapped. The ground
 * escaped the whole thing only because tileAt has always checked its
 * bounds first and answered water; the levels above and below never
 * checked, so they wrapped instead.
 *
 * The check therefore lives here, in one place, and every level lookup
 * goes through it rather than each one carrying its own copy of a
 * comparison that three of the four had forgotten to make.
 */
export function tileIndex(mapW: number, mapH: number, x: number, y: number): number {
  if (x < 0 || y < 0 || x >= mapW || y >= mapH) return -1;
  return y * mapW + x;
}

/**
 * A ground tile this character has never seen: no tile at all, as far as
 * the client knows, and drawn as the black of the unknown.
 *
 * Asked for on 2026-09-28: "don't send players the entire map. make them
 * have to explore and record it as they go". The welcome used to carry
 * every tile of the world, so the far zoom was a finished map from the
 * first step; now it carries what this character has seen — the server
 * keeps that with the character — and each tile comes the first time it
 * is seen (onSeen). A ground tile not yet sent is this, and tileOn answers
 * undefined for it, the answer the other levels always gave for a place
 * with no tile, so everything that already drew only what a level has
 * draws only what has been seen.
 */
export const UNSEEN = 255;

/**
 * The welcome's ground runs, decoded: `bin` is the base64 already undone,
 * a byte a character, each run a tile byte and then its count as an
 * unsigned varint, seven bits a byte, low bits first, the top bit set on
 * every byte but the last. Whatever the runs do not reach is UNSEEN.
 */
export function decodeRuns(bin: string, size: number): Uint8Array {
  const tiles = new Uint8Array(size).fill(UNSEEN);
  let at = 0;
  for (let i = 0; i < bin.length && at < size; ) {
    const t = bin.charCodeAt(i++);
    let n = 0;
    for (let shift = 0; i < bin.length; shift += 7) {
      const b = bin.charCodeAt(i++);
      n += (b & 0x7f) * 2 ** shift;
      if (!(b & 0x80)) break;
    }
    tiles.fill(t, at, Math.min(size, at + n));
    at += n;
  }
  return tiles;
}

/** How many revealed tiles are kept for the flat scene before it is told to paint afresh. */
const REVEALED_MAX = 50000;

/** One tile newly added to the map, for whatever keeps a picture of it. */
export interface Revealed {
  z: number;
  x: number;
  y: number;
  t: number;
}

/** All client-side game state; mutated by network messages and read by the renderer. */
/**
 * A tile the pointer picked: see Renderer.tileFromEvent. `through` says a
 * roof was looked through to the room under it, and `roof` is then the
 * roof's own tile (see pickThrough and Game.upOnRoof).
 */
export interface Picked {
  x: number;
  y: number;
  h?: number;
  through?: boolean;
  roof?: { x: number; y: number; z: number; h: number };
}

export class Game {
  myId = 0;
  mapW = 0;
  mapH = 0;
  /** The ground, a byte a tile, UNSEEN where this character has not been. */
  tiles: Uint8Array = new Uint8Array(0);
  /**
   * Tiles added since the welcome, in the order they came, for the flat
   * scene's pictures of each floor to be brought up to date from rather
   * than painted again (see Renderer.flatFloor). The renderer takes them
   * with takeRevealed; a new welcome starts it again, since the pictures
   * go with the ground the welcome brought.
   */
  private revealed: Revealed[] = [];
  private revealedOverflow = false;
  /** Every sparse level (z != 0): z -> (tile index -> tile). */
  levels = new Map<number, Map<number, number>>();
  /**
   * Level -> (tile index -> roof id): which building's roof an upper tile
   * belongs to, the tiles of each level above the ground grouped on their
   * own level. The ids are unique across levels. Only level 1 had roofs
   * until 2026-10-08, when the Prancing Pony went up at Bree with a room
   * over its ground floor and its thatch on level 2 ("put barliman
   * butterbur in a small inn at bree, with an upstairs that's a single
   * room with beds").
   */
  roofOf = new Map<number, Map<number, number>>();
  /** What roofLifts has found, by roof; emptied when the roofs are grouped again. */
  private roofLiftsMemo = new Map<number, boolean>();
  /**
   * Goes up whenever tiles come onto the map (a welcome, a reset, ground
   * seen for the first time), so what is worked out from the map can be
   * kept until it does: see the renderer's ground cache.
   */
  terrainEra = 0;
  pz: Rect[] = [];
  /** Protected ground under the ground, each patch on its own level only. */
  pzBelow: OpenFloor[] = [];
  /** The hollows under the ground open across levels: see openSpaceAt. */
  open: OpenSpace[] = [];
  /** Seconds of food a stomach holds; the full end of the food bar. */
  maxFood = 1;
  /** The names of the land and where to write them: see Welcome.places. */
  places: { name: string; x: number; y: number }[] = [];
  /** The scenery standing in the world: see Welcome.fixtures and groundShown. */
  fixtures: Fixture[] = [];
  /** The areas whose names are written on the map: those this character
   *  has seen any ground of (Welcome.known), and every one walked into
   *  since (you.area). */
  knownPlaces = new Set<string>();
  /** The places quests have sent this character to, drawn on the map at
   *  every zoom: from the welcome, and replaced whole by each "marks"
   *  (2026-10-08: "when npcs give quests, add the locations they mention
   *  to the map"). */
  marks: QuestMark[] = [];
  /** The world's people's menus, by the kind their creatures carry: what
   *  a click on one offers. From the welcome. */
  npcs = new Map<string, NpcMenu>();
  /** The quests on those menus this character has finished, by kind and
   *  place on the menu: from the welcome, and replaced with the marks. */
  topicsDone = new Map<string, Set<number>>();
  itemDefs = new Map<number, ItemDef>();
  /** Defs for piles whose variant names them differently from their kind,
   *  by id and name, made once each. */
  private namedDefs = new Map<string, ItemDef>();

  /**
   * What a pile is: its kind's def, called what the pile is called where
   * its variant names it differently (2026-10-21: a ring's number, "the
   * Third of the Seven Rings"). Everything else is its kind's def as it is.
   */
  defOf(it: { id: number; name?: string; def?: ItemDef }): ItemDef | undefined {
    // A made piece comes with what it is (a dwarf-wrought sword).
    if (it.def) return it.def;
    const def = this.itemDefs.get(it.id);
    if (!def || !it.name || it.name === def.name) return def;
    const key = `${it.id}|${it.name}`;
    let d = this.namedDefs.get(key);
    if (!d) {
      d = { ...def, name: it.name };
      this.namedDefs.set(key, d);
    }
    return d;
  }
  you: You | null = null;
  creatures = new Map<number, ClientCreature>();
  items: GroundItem[] = [];
  /** The fish on screen, with where each was a moment ago so it can be
   *  drawn sliding between tiles rather than hopping. */
  fish = new Map<number, { x: number; y: number; fromX: number; fromY: number; at: number; dir: string }>();
  /** Who the player is being shown the way to, wherever they are: a find
   *  person's minute, and the great rings while the One is worn. The
   *  window points an arrow at each from its edge. */
  tracks: Track[] = [];
  /** The lights of those in view the player cannot see, from the last
   *  state: drawn as any light is, with nobody drawn in them. */
  unseenLights: UnseenLight[] = [];
  /** The Unlights in view: see UnlightZone and unlitAt. */
  unlight: UnlightZone[] = [];
  /** Whether it is night above the ground, and how dark the sky is drawn
   *  there, nought to one: see State.night and State.sky. */
  night = false;
  skyDark = 0;
  /** Seconds until the sky turns, and whether an admin holds it: see
   *  State.turn and State.skyheld. */
  skyTurn = 0;
  skyHeld = false;
  floats: FloatingText[] = [];
  bubbles: SpeechBubble[] = [];
  /**
   * The level the window is looking at, or null to draw the one the
   * player is standing on. It is a look and not a move: nothing about the
   * character changes, and any step of their own puts the view back on
   * them (asked for on 2026-09-19: "When the player moves, it automatically
   * centres on the player again on their current level").
   *
   * The minimap had this and it went with the panel; it is back in the
   * window, where the ask for a button to look up and down a level put
   * it. What can be seen up there is the ground,
   * since the server sends creatures and items for the viewer's own
   * level and no other. Since 2026-10-13 it is drawn as the close view
   * seen from that floor, oblique and all — see Renderer.draw.
   */
  viewZ: number | null = null;
  /**
   * How far the view has been dragged off the player, in tiles, or null
   * for the ordinary case of the camera sitting on them (asked for on 2026-09-20:
   * "Support panning the view"). Fractional, because a drag is measured
   * in pixels and a pixel is a fraction of a tile at every zoom but the
   * closest.
   *
   * It lives here rather than in the renderer for the same reason viewZ
   * does: both are ways of looking somewhere other than at yourself, and
   * both end the moment you take a step, which is one rule in one place
   * (see onState) rather than two that have to be kept in step. The
   * renderer reads it and adds it to the camera; nothing else touches it.
   */
  pan: { x: number; y: number } | null = null;
  sparks: Spark[] = [];
  /** Throws heard from Sauron's sweep and not yet seen in a state, by
   *  creature: where they land and over how long (see "knocked"). */
  knocks = new Map<number, { x: number; y: number; ms: number; at: number }>();
  shots: Shot[] = [];
  /** The links of every chain lightning still on screen. See Arc. */
  arcs: Arc[] = [];
  /** Creature id -> when the player's last swing at it landed. */
  /**
   * The tile under the pointer, and the height in storeys of the surface
   * of it the pointer is on — a floor's own level, or the top of a wall —
   * for the outline to be drawn there rather than on the ground under it.
   */
  hoverTile: Picked | null = null;
  /**
   * The trade in hand, as the server last described it, or null when there
   * is none. It is the last message and nothing more: the client never
   * works out a trade's state for itself, since the server sends the whole
   * of it every time anything changes and an offer worked out locally
   * could only ever disagree with the one that binds.
   */
  trade: Trade | null = null;
  /**
   * This page's clock less the server's, in ms: what a server time is
   * added to for when it happened here, or null before the first reading.
   * It is the least over CLOCK_WINDOW_MS, so it includes the quickest
   * trip a message has made lately. See noteServerTime.
   */
  clockOffset: number | null = null;
  /** The readings the offset is the least of: a queue kept rising from the
   *  front, so the front is always the least still inside the window. */
  clockSamples: { at: number; off: number }[] = [];

  get ready() {
    return this.you !== null && this.mapW > 0;
  }

  /** Whether a trade window is open: the state in which the bag offers rather than uses. */
  get trading(): boolean {
    return this.trade?.state === "open";
  }

  /**
   * Take in a trade message. A closed trade is forgotten rather than kept
   * as a message that says closed, so the bag goes back to being a bag on
   * the same tick; the caller reads the reason before it is gone.
   */
  onTrade(t: Trade) {
    this.trade = t.state === "closed" ? null : t;
  }

  /** How many of one thing are in the player's own offer. */
  offered(want: Item): number {
    return (this.trade?.mine ?? []).find((i) => sameItem(i, want))?.count ?? 0;
  }

  /** How many of one thing the bag holds: the most that can be offered. */
  held(want: Item): number {
    return (this.you?.inv ?? []).filter((i: Item) => sameItem(i, want)).reduce((n, i) => n + i.count, 0);
  }

  /** Where (x, y) lives in a level of this map, or -1 for off the map. */
  private index(x: number, y: number): number {
    return tileIndex(this.mapW, this.mapH, x, y);
  }

  /**
   * What lies on the ground to be drawn and walked round: the items in
   * view, and every fixture of the map (Welcome.fixtures) on a tile that
   * has been seen, in view or not. Scenery is part of the map (2026-10-17:
   * "depot boxes should be part of the map, visible at distance";
   * 2026-11-02: "the seats of seeing and hearing, and other static objects
   * like depot boxes should be visible at any range on the map"), so it
   * stands on remembered ground as the walls round it do, on every level,
   * where the items in view stop at the edge of what the server sends.
   *
   * Where the server has sent scenery of its own on a tile, that is what
   * stands there and the fixture is left out: a box in view is not doubled,
   * and a door swung open is drawn open rather than open over shut.
   */
  groundShown(): GroundItem[] {
    if (!this.fixtures.length) return this.items;
    const at = (x: number, y: number, z: number) => ((z + 16) * this.mapH + y) * this.mapW + x;
    const sent = new Set<number>();
    for (const it of this.items) if (this.itemDefs.get(it.id)?.static) sent.add(at(it.x, it.y, it.z ?? 0));
    const out = this.items.slice();
    for (const f of this.fixtures) {
      const z = f.z ?? 0;
      if (sent.has(at(f.x, f.y, z)) || this.tileOn(z, f.x, f.y) === undefined) continue;
      out.push({ x: f.x, y: f.y, z, id: f.id, count: 1 });
    }
    return out;
  }

  /** The ground at (x, y): UNSEEN where it has not been seen, water off the map. */
  tileAt(x: number, y: number): number {
    const i = this.index(x, y);
    if (i < 0) return 4; // water, which nobody walks onto; the view draws the nearest edge tile there (see render.ts)
    return this.tiles[i];
  }

  /**
   * Whether the ground at (x, y) has never been seen, for the walk planner
   * to be hopeful about (2026-09-28: "allow map clicking into unexplored
   * areas"; see pathfind.ts). Off the map is not unexplored: it is nothing.
   */
  unexplored(x: number, y: number): boolean {
    const i = this.index(x, y);
    return i >= 0 && this.tiles[i] === UNSEEN;
  }

  /** The tile of the upper (rooftop) level at (x, y), or undefined for air. */
  tileUpperAt(x: number, y: number): number | undefined {
    return this.tileOn(1, x, y);
  }

  /** The tile of level z at (x, y): the ground map for 0, sparse levels else. */
  tileOn(z: number, x: number, y: number): number | undefined {
    if (z === 0) {
      const t = this.tileAt(x, y);
      return t === UNSEEN ? undefined : t;
    }
    const i = this.index(x, y);
    if (i < 0) return undefined; // off the map is air, not the next row along
    return this.levels.get(z)?.get(i);
  }

  /**
   * Which building's roof stands at (x, y), or undefined where none does.
   * The renderer asks this of tiles right across the window, edges of the
   * world included, so it is bounds-checked like every other level lookup:
   * see tileIndex.
   */
  roofAt(x: number, y: number, z = 1): number | undefined {
    const i = this.index(x, y);
    if (i < 0) return undefined;
    return this.roofOf.get(z)?.get(i);
  }

  /**
   * The roofs to hide for a viewer on level z at (x, y): the one over
   * their head, and any building they can see into — standing on its
   * doorway's threshold, or beside any open floor tile under its roof
   * (which is where windows would come in, if there were any).
   *
   * On the ground that is whatever is on level 1 over the room, a roof or
   * the storey of a building with a floor over its floor. Up a storey it
   * is only a roof, slate or thatch, over the room upstairs (2026-10-08,
   * the Prancing Pony's): the hilltops and shelves stacked over a
   * mountain's terraces are the mountain, not a roof, and stay where they
   * are.
   */
  hiddenRoofs(x: number, y: number, z = 0): Set<number> {
    const out = new Set<number>();
    for (const [dx, dy] of [[0, 0], [-1, 0], [1, 0], [0, -1], [0, 1]]) {
      const id = this.roofSeenInto(x + dx, y + dy, z);
      if (id !== undefined) out.add(id);
    }
    return out;
  }

  /**
   * The roof over (x, y) on level z+1 that a viewer on level z sees into
   * the room under from beside it, or undefined: see hiddenRoofs.
   *
   * Up a storey a mountain's hilltops are no roof, but a passage dug
   * through its rock is a room all the same: inside the Lonely Mountain,
   * on the stone floor and stairs down from its hidden door, the levels
   * over you are the mountain over your head, and go (2026-10-21: "when
   * standing inside erebor, we should be able to see the secret passage
   * inside, instead i see the levels above and my outline and can't see
   * where i'm going"). `passages` false leaves those out, for roofLifts:
   * walking the terraces outside, the mountain over you is the hill, and
   * stays.
   */
  private roofSeenInto(x: number, y: number, z: number, passages = true): number | undefined {
    const i = this.index(x, y);
    if (i < 0) return undefined;
    const over = this.levels.get(z + 1)?.get(i);
    if (over === undefined) return undefined;
    const ground = z === 0 ? this.tileAt(x, y) : this.tileOn(z, x, y) ?? UNSEEN;
    if (ground === UNSEEN) return undefined;
    const passage = (ground === Tile.Stone || ground === Tile.StairTop) && over !== Tile.Roof && over !== Tile.Thatch;
    if (z > 0 && over !== Tile.Roof && over !== Tile.Thatch && !(passages && passage)) return undefined;
    // The hall behind the Front Gate is such a passage on the ground.
    if (!passages && passage && (over === Tile.Rock || over === Tile.Hilltop)) return undefined;
    // Only an open tile lets you see in: a wall under the roof does not.
    if (ground === Tile.Water || ground === Tile.Wall || ground === Tile.Tree || ground === Tile.Rock || ground === Tile.FrozenWaste) return undefined;
    // Nor does the foot of a ramp or a slope: the tile over it is the
    // ramp's own head, the top of the one incline the two make, not a
    // roof over a room, and standing at the foot of a ramp lifted the
    // whole hilltop it climbs onto off the mountain (2026-09-28: "here's
    // an example of a ramp, just north of the tree, it isn't rendered
    // correctly at all").
    if (isRampFoot(ground)) return undefined;
    // Nor the foot of a stair, whose head over it is the top of its
    // flight (2026-09-28: "render stairs oblique, the one in the castle
    // for example"): standing at the foot of the stair up the dragons'
    // hill lifted the whole hilltop off, and left the flight climbing to
    // nothing.
    if (ground === Tile.Stair) return undefined;
    return this.roofOf.get(z + 1)!.get(i)!;
  }

  /**
   * Whether roof `id`, on level z, is a building's that a viewer on the
   * level under it could ever have lifted to see in: some tile of it is a
   * roof over a room (see roofSeenInto). A mountain's hilltops are over
   * its rock and never are. Kept until the roofs are grouped again.
   */
  roofLifts(id: number, z: number): boolean {
    let lifts = this.roofLiftsMemo.get(id);
    if (lifts !== undefined) return lifts;
    lifts = false;
    for (const [i, rid] of this.roofOf.get(z) ?? []) {
      if (rid === id && this.roofSeenInto(i % this.mapW, Math.floor(i / this.mapW), z - 1, false) === id) {
        lifts = true;
        break;
      }
    }
    this.roofLiftsMemo.set(id, lifts);
    return lifts;
  }

  /**
   * Under the ground, in a hollow open across levels, what of the level
   * over a viewer on level z at (x, y) to take off so they are not drawn
   * under it: the tiles of level z+1 that stand over open floor, reached
   * from the viewer's own tile or one beside it. Asked of a column (x, y)
   * of the map, whether it is one of them.
   * In the orcs' cavern that is the walk over the gateway when you stand
   * in it, and a corner tower's floor when you are in the room under it
   * (2026-10-19: "when under this, the palisade should not be visible
   * above us or the orc"). The walk over the palisade either side stays:
   * it is the top of a wall, not a roof over a floor, and it is where the
   * hollow's own walk goes on.
   */
  overheadBelow(x: number, y: number, z: number): (x: number, y: number) => boolean {
    const out = new Set<number>();
    const has = (nx: number, ny: number) => out.has(this.index(nx, ny));
    const over = this.levels.get(z + 1);
    if (z >= 0 || !over) return has;
    // A floor stood on, not a wall, the foot of a ramp or of a stair (the
    // tile over those is the top of the climb), or a tile not yet seen.
    const open = (nx: number, ny: number): boolean => {
      const ground = this.tileOn(z, nx, ny);
      return ground !== undefined && !BLOCKING_TILES.has(ground) && !isRampFoot(ground) && ground !== Tile.Stair;
    };
    const todo: [number, number][] = [];
    const take = (nx: number, ny: number) => {
      const i = this.index(nx, ny);
      if (i < 0 || out.has(i) || !over.has(i) || !open(nx, ny)) return;
      out.add(i);
      todo.push([nx, ny]);
    };
    take(x, y);
    take(x - 1, y);
    take(x + 1, y);
    take(x, y - 1);
    take(x, y + 1);
    while (todo.length > 0) {
      const [tx, ty] = todo.pop()!;
      take(tx - 1, ty);
      take(tx + 1, ty);
      take(tx, ty - 1);
      take(tx, ty + 1);
    }
    return has;
  }

  /**
   * Look a level up or down, clamped to the levels that exist, and back
   * to null the moment the choice lands on the player's own floor, so
   * "am I peeking" is one question with one answer rather than a level
   * that happens to match.
   */
  peekLevel(d: number) {
    const me = this.me();
    if (!me) return;
    const zs = [0, ...this.levels.keys()];
    const lo = Math.min(...zs), hi = Math.max(...zs);
    const next = Math.max(lo, Math.min(hi, (this.viewZ ?? me.z ?? 0) + d));
    this.viewZ = next === (me.z ?? 0) ? null : next;
  }

  /**
   * Drag the view by so many tiles, clamped so the camera stays over the
   * map. Without the clamp a long drag walks the view out into the
   * nothing past the edge, and since the only way back is a step of your
   * own, somebody who dragged too far on a character standing still
   * would be looking at a black screen with no way to say so.
   *
   * The camera is the player plus the pan, so the clamp is on the sum:
   * pan is free to be large when the player is at one edge and looking
   * at the other, and is pinned when it would take the centre of the
   * view off the map entirely.
   */
  panBy(dx: number, dy: number) {
    const me = this.me();
    if (!me) return;
    const p = this.pan ?? { x: 0, y: 0 };
    const cx = Math.max(0, Math.min(this.mapW - 1, me.x + p.x + dx));
    const cy = Math.max(0, Math.min(this.mapH - 1, me.y + p.y + dy));
    const next = { x: cx - me.x, y: cy - me.y };
    // Back on the player is not a pan at all, so "is the view off me" is
    // one question with one answer, as with viewZ landing on your floor.
    this.pan = next.x === 0 && next.y === 0 ? null : next;
  }

  /** Put the view back on the player without waiting for a step. */
  resetPan() {
    this.pan = null;
  }

  /**
   * Back to you: your own floor and the window centred on you, which is
   * what a step of your own does, without the step. The recentre mark in
   * the corner of the world asks for it (2026-09-24). The zoom is left
   * alone: that is the Reset view button's, and a player who has zoomed
   * out to see the country wants to come back to themselves at that size.
   */
  recentre() {
    this.viewZ = null;
    this.pan = null;
  }

  /** Whether the window is anywhere but on you, on your own floor. */
  offMe(): boolean {
    return this.viewZ !== null || this.pan !== null;
  }

  /**
   * Whether a tile is inside a protection zone: on the ground or above it,
   * one of pz; under the ground, one of pzBelow on its own level (Galion's
   * corner of the Elvenking's cellars, since 2026-10-07), as the server's
   * protectedLoc has it. Underground is outside every other law.
   */
  protectedAt(x: number, y: number, z = 0): boolean {
    // Asked of every tile in view several times a frame, so a tile of the
    // map is answered from a grid of the zones built once (pzGrid), and
    // only a point off the map or between tiles walks the rectangles.
    const grid = this.pzGrid();
    if (Number.isInteger(x) && Number.isInteger(y) && x >= 0 && y >= 0 && x < grid.w && y < grid.h) {
      const i = y * grid.w + x;
      return grid.below.get(z)?.[i] === 1 || (z >= 0 && grid.surface[i] === 1);
    }
    return this.protectedRects(x, y, z);
  }

  /**
   * The zones as one cell a tile, the ground's and each level's below it,
   * built again only when the zones or the map they lie on are new.
   */
  private pzCells: { pz: Rect[]; pzBelow: OpenFloor[]; w: number; h: number; surface: Uint8Array; below: Map<number, Uint8Array> } | null = null;
  private pzGrid() {
    const c = this.pzCells;
    if (c && c.pz === this.pz && c.pzBelow === this.pzBelow && c.w === this.mapW && c.h === this.mapH) return c;
    const w = this.mapW, h = this.mapH;
    const fill = (cells: Uint8Array, r: Rect) => {
      for (let y = Math.max(0, r.y); y < Math.min(h, r.y + r.h); y++) cells.fill(1, y * w + Math.max(0, r.x), y * w + Math.min(w, r.x + r.w));
    };
    const surface = new Uint8Array(w * h);
    for (const r of this.pz) fill(surface, r);
    const below = new Map<number, Uint8Array>();
    for (const r of this.pzBelow) {
      let cells = below.get(r.z);
      if (!cells) below.set(r.z, (cells = new Uint8Array(w * h)));
      fill(cells, r);
    }
    return (this.pzCells = { pz: this.pz, pzBelow: this.pzBelow, w, h, surface, below });
  }

  /** protectedAt the long way, by every rectangle: what the grid is built to agree with. */
  private protectedRects(x: number, y: number, z: number): boolean {
    const inside = (r: Rect) => x >= r.x && y >= r.y && x < r.x + r.w && y < r.y + r.h;
    // pzBelow's patches are each on their own level, the ground and above
    // included: the Prancing Pony's ground floor and not the room over it
    // (2026-10-08: "make only the inside of the inn a pz. do not make
    // upstairs a pz").
    if (this.pzBelow.some((r) => r.z === z && inside(r))) return true;
    if (z < 0) return false;
    return this.pz.some(inside);
  }

  /**
   * The hollow open across levels that (x, y) on level z is in, or
   * undefined: the orcs' cavern, whose yard and walk see each other in the
   * dark (2026-10-02: "Light like from the spell should apply to other
   * levels when underground"). Above the ground it is always undefined: the
   * sky is the surface's open space, and the renderer draws every level of
   * it already.
   */
  openSpaceAt(x: number, y: number, z: number): OpenSpace | undefined {
    if (z >= 0) return undefined;
    return this.open.find((s) => s.floors.some((f) => f.z === z && x >= f.x && y >= f.y && x < f.x + f.w && y < f.y + f.h));
  }

  /**
   * Whether the tile x, y on level z is in an Unlight: black whatever
   * lights it (2026-10-27). The square round where its maker stands, as
   * the server measures it (see unlight.go). A protection zone is never
   * in one (2026-11-02: "ungoliant's unlight should not extend into
   * protection zones"), unless an admin has the zones off.
   */
  unlitAt(x: number, y: number, z: number): boolean {
    for (const u of this.unlight) {
      if ((u.z ?? 0) === z && Math.max(Math.abs(x - u.x), Math.abs(y - u.y)) <= u.r) return !this.you?.zonesoff && !this.protectedAt(x, y, z);
    }
    return false;
  }

  me(): ClientCreature | undefined {
    return this.creatures.get(this.myId);
  }

  /**
   * The creature on the clicked tile, preferring the viewer's own level,
   * and on it anybody over an admin gone /invisible: one of them may share
   * a tile with anybody (2026-10-07: "allow them to coexist in the location
   * of another creature or player"), and a click there is for what stands
   * in the world, as it is for everybody who cannot see them at all.
   *
   * Nobody dead is on it (2026-10-22: "i can't seem to walk or pick things
   * up by clicking where a player corpse is"). A dead player lies where
   * they fell until they ask to respawn, and the server goes on sending
   * them, at no health, on the tile their body is on. Nothing draws them,
   * but this found them, so a click there followed (or a right-click
   * offered the menu of) somebody who was not there, and never walked or
   * took what lay on the body.
   */
  creatureAt(x: number, y: number, z?: number): ClientCreature | undefined {
    const myZ = this.me()?.z ?? 0;
    let best: ClientCreature | undefined;
    let bestRank = -1;
    for (const c of this.creatures.values()) {
      if (c.x !== x || c.y !== y || c.hp <= 0) continue;
      if (z !== undefined && (c.z ?? 0) !== z) continue;
      const rank = (c.z === myZ ? 2 : 0) + (c.invisible ? 0 : 1);
      if (rank > bestRank) {
        best = c;
        bestRank = rank;
      }
    }
    return best;
  }

  /**
   * The roof tile the pointer was on, when the pick looked through a roof
   * to the room under it and somebody or something is up on that roof
   * (2026-10-19: "make it so when you look from outside a building, and
   * the roof is shown, that we look at the stuff above, not the
   * corresponding tile on our level. so we look at someone standing right
   * on top of the mathom house if were outside of it and the roof was
   * shown"). A look, a click on a creature and the hover outline go to it.
   * A bare roof is still looked through to the room, as it was, so the
   * outline of somebody inside is still theirs to look at, and a click on
   * it still walks you in.
   */
  upOnRoof(p: Picked): { x: number; y: number; z: number; h: number } | undefined {
    const r = p.roof;
    if (!r) return undefined;
    const someone = [...this.creatures.values()].some((c) => c.x === r.x && c.y === r.y && (c.z ?? 0) === r.z && c.hp > 0);
    const something = this.items.some((i) => i.x === r.x && i.y === r.y && (i.z ?? 0) === r.z);
    return someone || something ? r : undefined;
  }

  /** Items on the clicked tile at the viewer's own level: the only ones in reach. */
  itemsAt(x: number, y: number): GroundItem[] {
    const myZ = this.me()?.z ?? 0;
    return this.items.filter((i) => i.x === x && i.y === y && (i.z ?? 0) === myZ);
  }

  /**
   * Whether there is anything on that tile worth bending down for. Blood,
   * spatter and bones are marks rather than things: the server reaches
   * straight past them for whatever is actually lying there, and finding
   * nothing it says nothing and does nothing.
   *
   * So a click has to ask this rather than merely whether the tile holds
   * items, or a step onto ground somebody bled on becomes a pickup of the
   * blood — which is refused in silence, leaving a player clicking a tile
   * they are standing next to and not moving. It is one question asked in
   * three places, and it lives here because the two that asked it
   * differently is what went wrong: the right-click knew about marks and
   * the left-click did not.
   *
   * What it asks is whether the tile holds anything that is not blood, and
   * not whether everything on it is decor — which is what it used to ask,
   * and which was wrong. A stirring pile of bones is decor: it cannot be
   * picked up and it is drawn as a mark. But it is the one mark in the
   * world you can *do* something with, since clicking it is how
   * sanctification is cast over it, and reading it as scenery made it
   * impossible to lay a skeleton to rest at all. Blood and spatter are the
   * marks that answer nothing, and the client already knows them by id
   * because it mixes their colours; so that is the question, asked of the
   * things it is actually about.
   *
   * A body is the same kind of mark (2026-09-27: "if i click on a corpse
   * when there's nothing else on the tile, the player does not move. it
   * should move"). The server reaches past it like blood, so a click that
   * sent a pickup for one alone found nothing, did nothing, and never
   * walked. What lies on a body is still picked up.
   */
  pickableAt(x: number, y: number): boolean {
    return this.itemsAt(x, y).some((it) => !BLOOD_RGB[it.id] && !SPATTER_RGB[it.id] && !isBody(it.id));
  }

  /**
   * Whether what a pickup here would reach is scenery that does nothing
   * when picked up: a bed, an anvil, a forge. The server reaches past the
   * marks to the top of what is left and, finding scenery like that, does
   * nothing, so a right-click or a long press that sent the pickup walked
   * over to it for nothing (2026-10-17: "Long pressing the beds in Bree
   * makes me walk over instead of doing a look?"). Such a tile gets the look.
   */
  idleSceneryAt(x: number, y: number): boolean {
    const top = this.itemsAt(x, y).filter((it) => !BLOOD_RGB[it.id] && !SPATTER_RGB[it.id] && !isBody(it.id)).pop();
    if (top?.show) return true; // a mathom on show is only looked at
    const def = top && this.itemDefs.get(top.id);
    return !!def?.static && !def.works;
  }

  /**
   * What a tap (a left click) or a hold (a long press, a right-click) on a
   * tile with no creature on it does, in one table, so the two gestures
   * cannot drift apart a thing at a time (2026-10-21: "i can't tap to move
   * onto a seat of amon hen on phone", then "this has happened before. can
   * we make this more consistent"). Blood, bodies, the beds and the seats
   * each came in on their own until then, each a predicate of its own in
   * one of the two listeners, and each time the other gesture or the other
   * distance was left doing nothing. The rule now is the one thing those
   * fixes had in common: a gesture never sends a pickup the server will do
   * nothing with.
   *
   *   on top (past the marks)        tap                    hold
   *   nothing, or only marks          walk there             look
   *   the undercroft's door           walk through it        swing it
   *   a chest, the apple tree         open it, from afar     open it
   *   scenery you stand on            walk onto it           look
   *     (a stool, a high seat)
   *   scenery that blocks             look beside you,       look
   *     (a bed, an anvil, a mathom)   else walk up to it
   *   anything else                   take it beside you,    take it, from afar
   *                                   else walk there
   *
   * Scenery is idle scenery (idleSceneryAt): static, and nothing happens
   * when it is picked up. beside is whether the tile is in reach, which a
   * hold never asks: the server walks a pickup up to what it is for.
   */
  tileAction(x: number, y: number, how: "tap" | "hold", beside = false): "pickup" | "walk" | "look" {
    if (this.doorAt(x, y)) return how === "tap" ? "walk" : "pickup";
    if (!this.pickableAt(x, y)) return how === "tap" ? "walk" : "look";
    if (this.opensAt(x, y)) return "pickup";
    if (this.idleSceneryAt(x, y)) {
      const top = this.itemsAt(x, y).filter((it) => !BLOOD_RGB[it.id] && !SPATTER_RGB[it.id] && !isBody(it.id)).pop();
      const standOn = !!top && !top.show && !this.itemDefs.get(top.id)?.blocking;
      if (how === "hold") return "look";
      return standOn || !beside ? "walk" : "look";
    }
    if (how === "hold") return "pickup";
    return beside ? "pickup" : "walk";
  }

  /**
   * Whether what a pickup here would reach opens like a chest: a quest
   * chest, the last apple tree, the Púkel-man. A left click on one that
   * is out of reach sends the pickup rather than a walk, and the server
   * walks you up and opens it on arriving, the way a click on an NPC
   * opens their menu on arriving (2026-10-21: "clicking a chest from a
   * distance should open it when you arrive (like for quests)"). A walk
   * planned to it ended beside a tile nobody can stand on, and there it
   * stopped.
   */
  opensAt(x: number, y: number): boolean {
    const top = this.itemsAt(x, y).filter((it) => !BLOOD_RGB[it.id] && !SPATTER_RGB[it.id] && !isBody(it.id)).pop();
    return !!(top && this.itemDefs.get(top.id)?.opens);
  }

  /**
   * Whether the tile holds the undercroft's door, shut or open. It is the
   * one pickable thing a left click does not pick: an open door is a
   * doorway, and a doorway is walked through, so the left button walks
   * and it is the right button — or a long press — that swings the door.
   * The server says which items are doors; the client does not know
   * their numbers.
   */
  doorAt(x: number, y: number): boolean {
    return this.itemsAt(x, y).some((it) => this.itemDefs.get(it.id)?.door);
  }

  onWelcome(w: Welcome) {
    this.myId = w.id;
    this.mapW = w.map.w;
    this.mapH = w.map.h;
    this.pz = w.pz ?? [];
    this.pzBelow = w.pzbelow ?? [];
    this.open = w.open ?? [];
    this.maxFood = Math.max(1, w.maxfood);
    this.places = w.places ?? [];
    this.fixtures = w.fixtures ?? [];
    this.knownPlaces = new Set(w.known ?? []);
    this.marks = w.marks ?? [];
    this.npcs = new Map(Object.entries(w.npcs ?? {}));
    this.topicsDone = topicsDoneOf(w.topicsdone);
    // The ground comes run-length encoded, a byte for the tile and then
    // its count as an unsigned varint: a fifth of the bytes a tile a byte
    // was, once the world grew to a thousand by eleven hundred and fifty
    // on 2026-09-28, and three bytes for the whole of a world nobody has
    // seen any of, since later that day ("don't send players the entire
    // map. make them have to explore and record it as they go") the ground
    // not yet seen comes as UNSEEN. See decodeRuns.
    this.tiles = decodeRuns(atob(w.map.runs), this.mapW * this.mapH);
    this.levels.clear();
    for (const lv of w.levels ?? []) this.levels.set(lv.z, new Map<number, number>());
    this.revealed = [];
    this.revealedOverflow = false;
    this.addTiles(w.levels ?? [], false);
    this.groupRoofs();
    this.itemDefs.clear();
    for (const d of w.items) this.itemDefs.set(d.id, d);
    setTwoHandedItems(w.items.filter((d) => d.twohanded).map((d) => d.id));
    this.creatures.clear();
    this.items = [];
    // A new connection is a new trip across the network, and a reading
    // from the old one could be quicker than anything this one manages.
    this.clockSamples = [];
    this.clockOffset = null;
  }

  /**
   * Take in tiles seen for the first time: put them on the map, keep them
   * for the flat scene's pictures, and group the roofs again if any of
   * them was a roof, since a building seen a row at a time is one roof
   * only once the rows meet.
   */
  /** Take the quest marks afresh: they replace what there was. */
  onMarks(m: Marks) {
    this.marks = m.marks ?? [];
    this.topicsDone = topicsDoneOf(m.topicsdone);
  }

  onSeen(s: Seen) {
    if (this.addTiles(s.levels, true)) this.groupRoofs();
  }

  /**
   * Take the map afresh: the ground and every level replaced by what the
   * server sends, as a welcome would, and nothing else about the game
   * touched (2026-09-28: "add a command /fullmap that gives the player the
   * entire map, and /nomap that removes it"). The flat scene's pictures are
   * painted again from scratch rather than brought up to date, since the
   * change can be the whole world at once, in either direction.
   */
  onMapReset(m: MapReset) {
    this.mapW = m.map.w;
    this.mapH = m.map.h;
    this.tiles = decodeRuns(atob(m.map.runs), this.mapW * this.mapH);
    this.levels.clear();
    for (const lv of m.levels) this.levels.set(lv.z, new Map<number, number>());
    this.addTiles(m.levels, false);
    this.revealed = [];
    this.revealedOverflow = true;
    this.groupRoofs();
  }

  /**
   * The tiles added since this was last asked, for the flat scene; or null
   * when so many have come since that the pictures are better painted
   * again than brought up to date, which is also what keeps the list from
   * growing for ever under somebody who never zooms out.
   */
  takeRevealed(): Revealed[] | null {
    const out = this.revealed;
    this.revealed = [];
    return this.revealedOverflow ? ((this.revealedOverflow = false), null) : out;
  }

  /**
   * Put tiles on the map, three numbers a tile, and say whether any of
   * them was on the rooftop level. `keep` is for the ones that arrive
   * after the welcome, which the flat scene's pictures do not have yet.
   */
  private addTiles(levels: LevelTiles[], keep: boolean): boolean {
    // A welcome or a reset is a new map; tiles seen on the way only move
    // the era if one of them is new to the map.
    if (!keep) this.terrainEra++;
    let roofs = false;
    for (const lv of levels) {
      let m: Map<number, number> | null = null;
      if (lv.z !== 0) {
        m = this.levels.get(lv.z) ?? new Map<number, number>();
        this.levels.set(lv.z, m);
      }
      const ts = lv.tiles;
      for (let k = 0; k + 2 < ts.length; k += 3) {
        const i = this.index(ts[k], ts[k + 1]);
        if (i < 0) continue;
        if (keep && (m ? m.get(i) : this.tiles[i]) !== ts[k + 2]) this.terrainEra++;
        if (m) m.set(i, ts[k + 2]);
        else this.tiles[i] = ts[k + 2];
        if (keep && !this.revealedOverflow) this.revealed.push({ z: lv.z, x: ts[k], y: ts[k + 1], t: ts[k + 2] });
      }
      if (lv.z === 1 && ts.length > 0) roofs = true;
    }
    if (this.revealed.length > REVEALED_MAX) {
      this.revealed = [];
      this.revealedOverflow = true;
    }
    return roofs;
  }

  /**
   * Group the upper tiles into connected roofs, one id per building, so
   * the renderer can lift a single building's roof to show the inside.
   * Again whenever a roof tile arrives, since a building seen a row at a
   * time is one roof only once its rows meet.
   */
  private groupRoofs() {
    this.roofOf.clear();
    this.roofLiftsMemo.clear();
    let nextRoof = 1;
    for (const [z, upper] of this.levels) {
      if (z > 0) nextRoof = this.groupLevel(upper, this.roofOf.set(z, new Map()).get(z)!, nextRoof);
    }
  }

  /** Group one level's tiles into connected roofs, numbered from nextRoof on: see groupRoofs. */
  private groupLevel(upper: Map<number, number>, roofOf: Map<number, number>, nextRoof: number): number {
    for (const start of upper.keys()) {
      if (roofOf.has(start)) continue;
      const id = nextRoof++;
      const queue = [start];
      roofOf.set(start, id);
      while (queue.length) {
        const i = queue.pop()!;
        // East and west only while there is a map that way. The run is
        // flat, so the tile "west of" column nought is the last column of
        // the row above it, and joining those two would make one roof out
        // of two buildings on opposite sides of the world — walk into one
        // and the other's roof would lift off. North and south cannot do
        // that: a whole row out either end is simply not in the level.
        const col = i % this.mapW;
        const near = [i - this.mapW, i + this.mapW];
        if (col > 0) near.push(i - 1);
        if (col < this.mapW - 1) near.push(i + 1);
        for (const n of near) {
          if (upper.has(n) && !roofOf.has(n)) {
            roofOf.set(n, id);
            queue.push(n);
          }
        }
      }
    }
    return nextRoof;
  }

  /**
   * Take a reading of the server's clock, sent at server (its Unix ms) and
   * arrived now (this page's): what the difference says, kept as the least
   * over the last CLOCK_WINDOW_MS.
   */
  noteServerTime(server: number, now: number) {
    const off = now - server;
    const q = this.clockSamples;
    while (q.length && q[q.length - 1].off >= off) q.pop();
    q.push({ at: now, off });
    while (q.length > 1 && now - q[0].at > CLOCK_WINDOW_MS) q.shift();
    this.clockOffset = q[0].off;
  }

  /**
   * Take in a batch of steps. Each is played from the moment it began by
   * the server's clock, carried onto this page's by the offset, plus the
   * playout delay for everybody but yourself; it is queued until then and
   * begun by advance. The steps are the authority on how anything moves —
   * when, how fast, from where — and the state is the check on them (see
   * onState).
   *
   * Your own steps get no delay (2026-09-28: "The player's OWN character
   * must not get the playout delay added to its responsiveness in a way
   * that makes input feel laggy: at least start its animation as soon as
   * its step message arrives"). The offset is the least lag seen, this
   * message's included, so a step of your own always begins no later than
   * the moment it arrives: on time when it came as fast as anything has,
   * and already a little way along when it was held up.
   *
   * A step for somebody not yet on screen is left alone: they have not
   * been introduced, and their first state says where they stand.
   */
  onSteps(m: Steps, now: number) {
    this.noteServerTime(m.now, now);
    const off = this.clockOffset ?? now - m.now;
    for (const s of m.s) {
      const c = this.creatures.get(s.id);
      if (!c) continue;
      const z = s.z ?? 0;
      const start = s.at + off + (s.id === this.myId ? 0 : PLAYOUT_DELAY_MS);
      c.queue.push({ x: s.x, y: s.y, z, dir: s.d, via: s.via, start, dur: s.ms });
      c.wireX = s.x;
      c.wireY = s.y;
      c.wireZ = z;
      c.wireDir = s.d;
    }
    this.advance(now);
  }

  /** Begin every queued step whose moment has come. Said every frame. */
  advance(now: number) {
    for (const c of this.creatures.values()) {
      const q = c.queue;
      if (!q.length) continue;
      while (q.length && q[0].start <= now) this.play(c, q.shift()!);
      if (!q.length) {
        // The last step heard has begun, so the creature faces whichever
        // way the server last said, which a state can have turned it to
        // since that step went out.
        c.dir = c.wireDir;
        c.lastDir = DIR_INDEX[c.wireDir] ?? c.lastDir;
      }
    }
  }

  /** Begin one step: from wherever the last one ended, or from the passage
   *  a level-changing one went through, at its own time and over its own
   *  length. */
  private play(c: ClientCreature, s: QueuedStep) {
    if (c.id === this.myId && (c.x !== s.x || c.y !== s.y || (c.z ?? 0) !== s.z)) {
      // A step of your own ends the look at another floor and the drag
      // away from yourself: see onState.
      this.viewZ = null;
      this.pan = null;
    }
    c.fromX = s.via ? s.via.x : c.x;
    c.fromY = s.via ? s.via.y : c.y;
    c.x = s.x;
    c.y = s.y;
    c.z = s.z;
    c.dir = s.dir;
    c.lastDir = DIR_INDEX[s.dir] ?? c.lastDir;
    c.via = s.via;
    c.move = s.dur;
    c.moveStart = s.start;
    c.moveDur = s.dur;
  }

  /**
   * Put a creature where a state says it is, when the steps did not: the
   * way every move was drawn before the steps had their own messages. One
   * tile off is animated from where it was drawn, over the step the server
   * says it took; further is a jump.
   */
  /** Whether a throw by Sauron's sweep was heard for creature id, landing
   *  where c now stands, in the last second. */
  private knockedTo(id: number, c: Creature, now: number): boolean {
    const k = this.knocks.get(id);
    if (!k) return false;
    if (now - k.at > 1000 || k.x !== c.x || k.y !== c.y) {
      this.knocks.delete(id);
      return false;
    }
    return true;
  }

  private snap(prev: ClientCreature, c: Creature, now: number) {
    if (prev.x !== c.x || prev.y !== c.y || (prev.z ?? 0) !== (c.z ?? 0)) {
      const dx = Math.abs(prev.x - c.x), dy = Math.abs(prev.y - c.y);
      if (c.via) {
        // Through a passage: the walker stepped onto the stairs, hole or
        // ladder and the portal put them out one tile the way it faces, a
        // level along. Animate that last tile, from the passage rather
        // than from where they left — which is a storey and two tiles
        // back — and take the whole of the longer step doing it, so the
        // climb reads as the slow thing it is.
        prev.fromX = c.via.x;
        prev.fromY = c.via.y;
        prev.moveStart = now;
        prev.moveDur = c.move || c.step * LEVEL_CHANGE_STEPS;
      } else if (Math.max(dx, dy) === 1) {
        // A single step: animate from the previous tile, over as long as
        // the step really takes. The server sends that; the sum after
        // the `||` is what to do without it, and knows nothing about
        // what was walked on.
        prev.fromX = prev.x;
        prev.fromY = prev.y;
        prev.moveStart = now;
        prev.moveDur = c.move || c.step * (dx && dy ? 2.5 : 1);
      } else if (this.knockedTo(prev.id, c, now)) {
        // Thrown by Sauron's sweep: flown from where they stood, fast.
        prev.fromX = prev.x;
        prev.fromY = prev.y;
        prev.moveStart = now;
        prev.moveDur = this.knocks.get(prev.id)!.ms;
        this.knocks.delete(prev.id);
      } else {
        // Teleport (death/respawn or first sight): snap.
        prev.fromX = c.x;
        prev.fromY = c.y;
        prev.moveDur = 0;
      }
    }
    prev.x = c.x;
    prev.y = c.y;
    prev.z = c.z;
    prev.dir = c.dir;
    prev.lastDir = DIR_INDEX[c.dir] ?? prev.lastDir;
    prev.move = c.move;
    // Left off the wire rather than sent empty, so cleared by hand: a stale
    // via would animate an ordinary jump as a climb.
    prev.via = c.via;
  }

  onState(s: State, now: number) {
    if (s.now !== undefined) this.noteServerTime(s.now, now);
    this.you = s.you;
    // An area walked into is one whose name the far zoom writes from now
    // on, as the welcome's known areas are (2026-10-05).
    if (s.you?.area) this.knownPlaces.add(s.you.area);
    this.tracks = s.tracks ?? [];
    this.unseenLights = s.lights ?? [];
    this.unlight = s.unlight ?? [];
    this.night = !!s.night;
    this.skyDark = (s.sky ?? 0) / 100;
    this.skyTurn = s.turn ?? 0;
    this.skyHeld = !!s.skyheld;
    const seen = new Set<number>();
    for (const c of s.creatures) {
      seen.add(c.id);
      const prev = this.creatures.get(c.id);
      if (!prev) {
        this.creatures.set(c.id, {
          ...c, fromX: c.x, fromY: c.y, moveStart: now, moveDur: 0, lastDir: DIR_INDEX[c.dir] ?? 2,
          queue: [], wireX: c.x, wireY: c.y, wireZ: c.z ?? 0, wireDir: c.dir,
        });
        continue;
      }
      // The steps are the authority on motion and the state is the check
      // on them (2026-09-28: "if a creature's snapshot position disagrees
      // with where the steps put it after they've played out, snap to the
      // snapshot. Make sure a late snapshot cannot pull a creature back a
      // tile"). The server sends the steps a state shows before the state
      // itself, so the check is against where the steps heard so far leave
      // the creature — not where it is being drawn, which is up to a step
      // and the playout delay behind that. Agreeing, the state touches
      // nothing about where it is; disagreeing — a shove, a death, the
      // temple, a step taken out of sight — it is the state that is right,
      // and anything still queued is dropped.
      const agrees = prev.wireX === c.x && prev.wireY === c.y && prev.wireZ === (c.z ?? 0);
      if (agrees) {
        // Only which way it faces, which can change without a step — a
        // turn on the spot, a monster squaring up to its prey — and which
        // waits for the steps still queued to finish, so a walker does not
        // turn round in the middle of the tile it is crossing.
        prev.wireDir = c.dir;
        if (!prev.queue.length) {
          prev.dir = c.dir;
          prev.lastDir = DIR_INDEX[c.dir] ?? prev.lastDir;
        }
      } else {
        prev.queue.length = 0;
        prev.wireX = c.x;
        prev.wireY = c.y;
        prev.wireZ = c.z ?? 0;
        prev.wireDir = c.dir;
        if (c.id === this.myId && (prev.x !== c.x || prev.y !== c.y || (prev.z ?? 0) !== (c.z ?? 0))) {
          // A step of your own ends the look at another floor, and the drag
          // away from yourself with it. It is the whole bargain of both:
          // look where you like, and moving brings the window back to you,
          // on the level you are standing on and centred on your feet.
          this.viewZ = null;
          this.pan = null;
        }
        this.snap(prev, c, now);
      }
      // Everything else the state says is the state's. Where the creature
      // is, which way it faces and the step it is taking are the steps'
      // (above), so they are left out of the merge.
      const { x: _x, y: _y, z: _z, dir: _dir, via: _via, move: _move, ...rest } = c;
      Object.assign(prev, rest);
      // Both of these are left off the wire rather than sent empty, and a
      // merge only overwrites what it is given, so they are cleared by hand:
      // a stale peek would leave someone lying in a hole they climbed out
      // of.
      prev.peek = c.peek;
      // And the aggro rings, for a third version of the same reason: they
      // only ride along while the debug switch is on, so a merge that
      // left the last pair in place would keep drawing rings round every
      // monster on screen after the switch went off, frozen at whatever
      // they read at the moment it did.
      prev.alert = c.alert;
      prev.clank = c.clank;
      // And their light, for a fourth: it is off the wire above ground
      // and on a monster, and a merge that kept the last number would
      // leave a lamp burning on somebody who has climbed out of the dark.
      prev.light = c.light;
      prev.tint = c.tint;
      prev.tintr = c.tintr;
      // And the grey of an admin gone /invisible, for a fifth: an admin
      // coming back into sight has to stop being drawn as a ghost.
      prev.invisible = c.invisible;
      // And somebody's ally, for a ninth: a convince that wears off has to
      // turn its name back to a monster's red.
      prev.ally = c.ally;
      prev.allycolor = c.allycolor;
      // And how faded they are, for a sixth: a ring taken off fades back
      // to nought, which is left off the wire.
      prev.fade = c.fade;
      // And a great ring's outline, which goes when the ring comes off.
      prev.greatring = c.greatring;
      // And a Nazgûl's steed, which goes when it is thrown.
      prev.steed = c.steed;
      // And the makes in hand, for a seventh: putting a dwarf-wrought
      // sword away for a plain one has to take its outline with it.
      prev.wmake = c.wmake;
      prev.smake = c.smake;
      prev.amake = c.amake;
      prev.hmake = c.hmake;
      prev.cmake = c.cmake;
      // And the belt, for an eighth: it is off the wire when there is none,
      // and taking it off has to take it off the picture.
      prev.belt = c.belt;
    }
    for (const id of this.creatures.keys()) {
      if (!seen.has(id)) {
        this.creatures.delete(id);
      }
    }
    this.items = s.items;
    // Fish slide from where they were to where they are; anything that has
    // swum off screen is forgotten rather than left hanging at the edge.
    const here = new Set<number>();
    for (const f of s.fish ?? []) {
      here.add(f.id);
      const prev = this.fish.get(f.id);
      if (prev && (prev.x !== f.x || prev.y !== f.y)) {
        this.fish.set(f.id, { x: f.x, y: f.y, fromX: prev.x, fromY: prev.y, at: now, dir: f.dir });
      } else if (!prev) {
        this.fish.set(f.id, { x: f.x, y: f.y, fromX: f.x, fromY: f.y, at: now, dir: f.dir });
      } else {
        prev.dir = f.dir;
      }
    }
    for (const id of this.fish.keys()) {
      if (!here.has(id)) this.fish.delete(id);
    }
  }

  onText(t: Text, now: number) {
    if ((t.kind === "say" || t.kind === "noise" || t.kind === "answer" || t.kind === "critical") && t.id) {
      // One head, one line. An answer takes the slot like anything else,
      // which is what should happen to the incantation a find person said
      // out loud a beat earlier: the question is replaced by its answer.
      this.bubbles = this.bubbles.filter((b) => b.id !== t.id);
      // Longer lines stay up longer, as in Tibia, so there is time to read
      // them. The longest line in the world goes through here — a look at a
      // player in full kit, with their deaths and their skull on it — and
      // wants every bit of the ten seconds the cap allows, which is what
      // the cap already gives it. A separate, longer clock for answers was
      // the alternative and buys nothing: ten seconds is already past the
      // point where a sentence left over your head is in the way.
      // An answer hangs only its first sentence when the server gives
      // one (2026-10-20), and the log has the whole of it.
      const text = (t.kind === "answer" && t.bubble) || t.text;
      const ttl = Math.min(3000 + text.length * 80, 10000);
      this.bubbles.push({
        id: t.id,
        x: t.x ?? 0,
        y: t.y ?? 0,
        // A line carries no level on the wire; the speaker's, when they
        // are in sight, is where the words were said.
        z: this.creatures.get(t.id)?.z,
        text,
        // Both kinds that become a bubble carry a name on the wire, so the
        // fallback is for a malformed message rather than for a speaker
        // nobody is: an empty name is drawn as no name, never as a stray
        // colon standing in for one.
        from: t.from ?? "",
        born: now,
        ttl,
        kind: t.kind,
        spell: t.spell,
        yell: t.yell,
      });
    }
  }

  /**
   * Floats stagger per tile: a newcomer starts far enough below the ones
   * still in the air that nothing is ever covered, and rises after them —
   * a burst of hits reads as a little column instead of one smudge.
   */
  /**
   * Add to the float of this kind already over the tile, or start one. It is
   * for gains that arrive in a flurry — experience from a pack cut down in
   * one swing, say — which read better as one growing number than as a
   * column of small ones waiting their turn. A float that has already had
   * most of its life is left alone and a new one started under it, so a
   * total never changes just as it is fading out.
   *
   * within is how long a float keeps accepting additions, and restart says
   * whether each addition buys the number a fresh read. The two together
   * are what tell a burst from a stream. A burst — experience off two kills
   * landing at once — is over as soon as it started, so it takes the
   * default: keep adding for as long as the number is not already fading,
   * and give the new total its full life each time, since there is no
   * danger of it growing forever.
   *
   * A stream is the other thing. Healing over time arrives every tick of
   * the server's clock, which is ten times a second, and under the burst
   * rule every one of those would push the total's life out again and the
   * number would sit over your head growing for as long as the healing
   * lasted. So a stream measures its window from the float's own birth and
   * does not restart it: it gathers a second's worth, and the next tick
   * after that starts a fresh number under it.
   */
  private mergeFloat(
    key: string,
    f: Omit<FloatingText, "drop" | "merge">,
    say: (n: number) => string,
    opts: { within?: number; restart?: boolean } = {},
  ) {
    const within = opts.within ?? f.ttl / 2;
    const restart = opts.restart ?? true;
    for (const o of this.floats) {
      if (o.merge !== key || o.x !== f.x || o.y !== f.y) continue;
      if (f.born - o.born > within) continue;
      o.value = (o.value ?? 0) + (f.value ?? 0);
      o.text = say(o.value);
      if (restart) o.born = f.born; // the combined figure gets a full read of its own
      return;
    }
    this.pushFloat({ ...f, merge: key });
  }

  private pushFloat(f: Omit<FloatingText, "drop">) {
    let drop = 0;
    for (const o of this.floats) {
      if (o.x !== f.x || o.y !== f.y) continue;
      const age = (f.born - o.born) / o.ttl;
      if (age >= 1) continue;
      // Where the older float sits right now, relative to the tile's
      // usual starting spot: its own drop, less how far it has risen.
      // Far enough under the one already there not to touch it, which
      // depends on how big that one is.
      const at = o.drop - age * 28;
      drop = Math.max(drop, at + (o.size ?? 12) + 1);
    }
    // But never further down than one row under the usual spot. Each new
    // number went under the last for as long as the last was in the air,
    // so a crowd hitting the Balrog, or one figure taking claws, fire and
    // the burning floor all at once, built a column running down the
    // screen, the newest numbers three tiles south of whoever was being
    // hit (2026-10-20: "the issue is the numbers are south of the actual
    // characters by about 3 tiles"). Past that row the ones already up
    // are lifted instead, so the column grows upwards, the way the
    // numbers are going anyway, and the newest stays on the figure.
    if (drop > FLOAT_MAX_DROP) {
      const lift = drop - FLOAT_MAX_DROP;
      for (const o of this.floats) if (o.x === f.x && o.y === f.y) o.drop -= lift;
      drop = FLOAT_MAX_DROP;
    }
    this.floats.push({ ...f, drop });
  }

  /**
   * Whoever the server last put on a tile, which is who a blow landing
   * there was dealt to: where the steps heard so far leave them, not where
   * they are drawn, since the effect comes from the same server moment as
   * those steps.
   */
  private standingOn(x: number, y: number, z: number): number | undefined {
    for (const c of this.creatures.values()) {
      if (c.hp > 0 && c.wireX === x && c.wireY === y && c.wireZ === z) return c.id;
    }
    return undefined;
  }

  onEffect(e: Effect, now: number) {
    const floats = this.floats.length, sparks = this.sparks.length, shots = this.shots.length, arcs = this.arcs.length;
    switch (e.kind) {
      case "dmg":
        // A player's damage lands in their own colour; a monster's is
        // always red, and no player is ever dealt a red-like colour.
        this.pushFloat({ x: e.x, y: e.y, text: num(e.value ?? 0), color: e.color || "#f33", born: now, ttl: 1100 });
        break;
      case "heal":
        // Mending arrives a tick at a time — a potion pays out ten times a
        // second for three seconds — and thirty separate +2s climbing over
        // one another says nothing except that something is happening. So a
        // second's worth is gathered into one number and the second after
        // that starts another under it: you read "+25" once a second, which
        // is both the rate and the amount, instead of a flicker you cannot
        // count. A single heal that arrives on its own is unaffected; there
        // is nothing for it to gather.
        //
        // A value of nothing does not travel — the field is left out of
        // the message — so it has to be read as nothing rather than as
        // whatever "+" and an undefined make between them.
        //
        // A player mends in green; a monster drinking what it took out of
        // somebody comes with its own colour, red, and gathers apart from
        // any green on the same tile.
        this.mergeFloat(
          "heal" + (e.color ?? ""),
          { x: e.x, y: e.y, text: "+" + num(e.value ?? 0), color: e.color || "#5f5", born: now, ttl: HEAL_TTL, value: e.value ?? 0 },
          (n) => "+" + num(n),
          { within: HEAL_GATHER_MS, restart: false },
        );
        break;
      case "mana":
        // Mana back faster than the ordinary (2026-10-17: "show popup
        // numbers for accelerated mana too"): a blue +X over your own
        // head, the mana bar's blue, gathered as the heal's green is.
        this.mergeFloat(
          "mana",
          { x: e.x, y: e.y, text: "+" + num(e.value ?? 0), color: "#6cf", born: now, ttl: HEAL_TTL, value: e.value ?? 0 },
          (n) => "+" + num(n),
          { within: HEAL_GATHER_MS, restart: false },
        );
        break;
      case "miss":
        this.sparks.push({ x: e.x, y: e.y, kind: "miss", born: now });
        break;
      case "crit":
        // A critical blow (asked for on 2026-09-27: "implement crits ...
        // show something when a crit occurs"): a CRIT! rising off the one
        // it landed on, bigger than a damage number and ahead of it, so the
        // number that follows is read as the critical's. It is drawn in the
        // colour of whoever landed it, the same as that one's damage
        // numbers (asked for on 2026-10-07: "when a crit is inflicted, the
        // color of the crit text should be based on who inflicted the
        // crit"): a player's own, and the monster red when no colour comes
        // with it — a monster's crit, or one from a server that never sent
        // a colour, drawn as a monster's rather than as anybody's in
        // particular. The dark outline every float gets keeps it legible.
        this.pushFloat({ x: e.x, y: e.y, text: "CRIT!", color: e.color || "#f33", born: now, ttl: 1400, size: 15 });
        break;
      case "wide":
        // A shot that went wide (asked for on 2026-09-23: 'on a miss show
        // "*miss*" as a pop-up text'; any ranged shot can since 2026-10-08,
        // nine in ten landing at two to four tiles and three in four
        // elsewhere). It rises off the tile like a damage number, since it
        // stands where a number would have been, and in a pale grey rather
        // than anybody's colour: nobody dealt it.
        this.pushFloat({ x: e.x, y: e.y, text: "*miss*", color: "#ccc", born: now, ttl: 1100 });
        break;
      case "block":
        this.sparks.push({ x: e.x, y: e.y, kind: "block", born: now });
        break;
      case "spark":
        // A blow stopped by armor alone, nothing raised to meet it.
        this.sparks.push({ x: e.x, y: e.y, kind: "spark", born: now });
        break;
      case "immune":
        // A blow that arrived and did nothing because the thing is immune
        // to what it was made of: a fire sword on a dragon. Asked for on
        // 2026-10-09: "make sure that attacking a creature immune to fire
        // with a firesword either sparks or shows some indication that it
        // can't be damaged like that". It sparks, since the blade met it,
        // and says so in fire's orange, so it is not read as a swing that
        // went past or as armor that will give way to a harder one.
        this.sparks.push({ x: e.x, y: e.y, kind: "spark", born: now });
        this.pushFloat({ x: e.x, y: e.y, text: "immune", color: "#ff9a3c", born: now, ttl: 1400 });
        break;
      case "blood":
        // A blow that went in. The value is the spatter the victim
        // bleeds, so the puff is drawn in the same colour as the marks it
        // leaves on the ground: they are the same fluid a moment apart.
        this.sparks.push({ x: e.x, y: e.y, kind: "blood", born: now, id: e.value ?? 0 });
        break;
      case "xp":
        // Experience, in white, over your own head and nobody else's. Two
        // kills landing together make one number rather than a queue.
        this.mergeFloat("xp", {
          x: e.x, y: e.y, text: xpText(e.value ?? 0), color: "#fff",
          born: now, ttl: 2400, value: e.value ?? 0, size: XP_SIZE,
        }, xpText);
        break;
      case "break": {
        // A piece of gear gave out. The pieces fly for everyone watching, and
        // the name says whose loss it was, since the bag itself is off screen.
        const name = this.itemDefs.get(e.value ?? 0)?.name ?? "item";
        this.sparks.push({ x: e.x, y: e.y, kind: "break", born: now });
        this.pushFloat({ x: e.x, y: e.y, text: `${name} breaks!`, color: "#ddd", born: now, ttl: 2400 });
        break;
      }
      case "sanctify":
        // A pile of bones put down for good. Pale gold, the temple's own
        // colour rather than the smith's, and said plainly: what matters
        // is that this pile is finished rather than that a spell was cast.
        this.pushFloat({ x: e.x, y: e.y, text: "at rest", color: "#e8dca0", born: now, ttl: 2400 });
        // And the spell seen going off over them: see "sanctify" in render.ts.
        this.sparks.push({ x: e.x, y: e.y, kind: "sanctify", born: now });
        break;
      case "crown":
        // Durin's Crown, seen in the Mirrormere by whoever looked into it:
        // see "crown" in render.ts.
        this.sparks.push({ x: e.x, y: e.y, kind: "crown", born: now });
        break;
      case "attack":
        // Each swing is reported, but what it lands on is outlined from
        // the state's foe instead, which stays for as long as the swings
        // do; there is nothing to flash.
        break;
      case "shot":
        // The value names the item that is flying, and its definition says
        // how it is drawn: see missileKind for why it is done by a word on
        // the definition rather than by a table of ids.
        if (e.id) this.shots.push({ from: e.id, x: e.x, y: e.y, born: now, kind: missileKind(this.itemDefs.get(e.value ?? 0)) });
        break;
      case "bluefire":
        // The Blue Tower's blue fire (2026-11-01), thrown by an acolyte or
        // the Blue Wizard: drawn as the staff's missile is.
        if (e.id) this.shots.push({ from: e.id, x: e.x, y: e.y, born: now, kind: "bluefire" });
        break;
      case "lava":
        // A geomancer's bolt: thrown like an arrow, drawn like an ember.
        if (e.id) this.shots.push({ from: e.id, x: e.x, y: e.y, born: now, kind: "lava" });
        break;
      case "whip":
        // A Balrog's whip, lashed out to where its target stands
        // (2026-10-20: "the target should be entangled on the spot for a
        // moment then pulled back after the whip retracts and then
        // entangled again for a moment"): it goes out, holds them there
        // for `value` milliseconds, pulling back to the Balrog over the
        // end of that, and then the server drags them in, which "coiled"
        // follows.
        if (e.id) this.shots.push({ from: e.id, x: e.x, y: e.y, born: now, kind: "whip", ms: Math.max(e.value ?? 0, WHIP_MS) });
        this.sparks.push({ x: e.x, y: e.y, kind: "coiled", born: now + WHIP_MS, ms: Math.max((e.value ?? 0) - WHIP_MS - WHIP_RETRACT_MS, 1) });
        break;
      case "coiled":
        // Whoever the whip dragged in, held where they landed for `value`
        // milliseconds with its coils still round their legs.
        this.sparks.push({ x: e.x, y: e.y, kind: "coiled", born: now, ms: e.value ?? 0 });
        break;
      case "sweep": {
        // A tile of Sauron's mace sweep (2026-11-01), swung from where he
        // stands: the three arrive together, so the arc reads as one blow.
        const by = e.id ? this.creatures.get(e.id) : undefined;
        this.sparks.push({ x: e.x, y: e.y, kind: "sweep", born: now, fx: by?.x, fy: by?.y });
        break;
      }
      case "knocked":
        // Whoever the sweep threw, landing at x/y. This comes ahead of the
        // state that moves them, which would draw a move of more than a
        // tile as a jump; snap looks here first and draws it as a flight.
        if (e.id) this.knocks.set(e.id, { x: e.x, y: e.y, ms: e.value ?? 300, at: now });
        break;
      case "knockdown":
        // Lying where the sweep threw them, for `value` ms, from when the
        // flight heard just before it ends.
        {
          const k = [...this.knocks.values()].find((k) => k.x === e.x && k.y === e.y && now - k.at < 100);
          const flight = k ? k.ms : 0;
          this.sparks.push({ x: e.x, y: e.y, kind: "knockdown", born: now + flight, ms: Math.max((e.value ?? 0) - flight, 1) });
        }
        break;
      case "lure":
        // A corpse candle's light, reaching out to where whoever it drew
        // in was standing: the server has them beside it in the same
        // tick, as with the whip, and paralysed for a moment there.
        if (e.id) this.shots.push({ from: e.id, x: e.x, y: e.y, born: now, kind: "lure" });
        break;
      case "arc": {
        // One link of a chain lightning: it leapt out of creature e.id and
        // landed on the tile e.x, e.y. Both ends are pinned here and now
        // rather than followed, because the thing it leapt out of may be
        // dead before the drawing is over — see Arc for the argument.
        //
        // The creature's own tile is used rather than where it is drawn
        // mid-step, which is deliberate and not laziness about the walking
        // interpolation: the server dealt this from the tile it has the
        // creature on, and a bolt drawn leaving a body a third of the way
        // into its next step would be a picture of something the server
        // did not do.
        const src = e.id ? this.creatures.get(e.id) : undefined;
        if (src) this.arcs.push({ fx: src.x, fy: src.y, x: e.x, y: e.y, born: now });
        break;
      }
      case "shaft": {
        // A Istari light: thrown out of creature e.id to the tile e.x, e.y,
        // the far end of the shaft. Pinned at both ends, as an arc is.
        const src = e.id ? this.creatures.get(e.id) : undefined;
        if (src) this.arcs.push({ fx: src.x, fy: src.y, x: e.x, y: e.y, born: now, shaft: true });
        break;
      }
      case "song":
        // A siren, across the water. It travels from the creature like an
        // arrow and a bolt do, and for the same reason: what is worth
        // knowing is which of the water it came out of, and a siren is
        // often the one thing on the screen you cannot walk up to.
        if (e.id) this.shots.push({ from: e.id, x: e.x, y: e.y, born: now, kind: "song" });
        break;
      case "shriek":
        // The Witch-king's shriek (2026-10-12): one message on his tile,
        // with how far it carried in `value`, drawn as rings riding out
        // to that edge. Whoever it caught learns it from their status line.
        this.sparks.push({ x: e.x, y: e.y, kind: "shriek", born: now, r: e.value ?? 0 });
        break;
      case "lull":
        // Old Man Willow's song (2026-10-12): one message on his tile, with
        // how far it carried in `value`, drawn as slow rings drifting out to
        // that edge. Whoever it caught learns it from their status line.
        this.sparks.push({ x: e.x, y: e.y, kind: "lull", born: now, r: e.value ?? 0 });
        break;
      case "smoke":
        // A pipe of Longbottom Leaf (2026-10-16; see longbottom.go): a
        // smoke ring rising off whoever lit it, on the tile X, Y.
        this.sparks.push({ x: e.x, y: e.y, kind: "smoke", born: now });
        break;
      case "vanish":
        // An admin's /vanish (2026-10-27; see vanish.go): a burst of
        // smoke and a flash where they stood, on the tile X, Y.
        this.sparks.push({ x: e.x, y: e.y, kind: "vanish", born: now });
        break;
      case "summon":
        // An admin's /summon (2026-10-27; see summonMonster): a ring of
        // light drawing in where it arrived, on the tile X, Y. A value of
        // 1 is a thing rather than a creature, drawn smaller.
        this.sparks.push({ x: e.x, y: e.y, kind: "summon", born: now, r: e.value ?? 0 });
        break;
      case "regen":
        // A beat of mending faster than the ordinary in the Party Tree's
        // flowers (2026-10-17). Sent only on a beat that closed something,
        // to everyone who can see the tile X, Y.
        this.sparks.push({ x: e.x, y: e.y, kind: "regen", born: now });
        break;
      case "web":
        // Shelob's web (2026-10-12): thrown from her, e.id, onto the tile
        // e.x, e.y, and left lying there for as long as it entangles
        // whoever it caught, which is `value` milliseconds.
        if (e.id) this.shots.push({ from: e.id, x: e.x, y: e.y, born: now, kind: "web" });
        this.sparks.push({ x: e.x, y: e.y, kind: "webbed", born: now, ms: (e.value ?? 0) + WEB_FLIGHT_MS });
        break;
      case "rift":
        // One per tile of a demon's burst, every tile within three of it.
        // They arrive in the same instant, so the ground opens all at once
        // and shuts as one thing.
        this.sparks.push({ x: e.x, y: e.y, kind: "rift", born: now });
        break;
      case "fire":
        // One per tile of a dragon's wave. They arrive together, so the
        // whole wave lights at once and dies down as one.
        this.sparks.push({ x: e.x, y: e.y, kind: "fire", born: now });
        break;
      case "death":
        this.sparks.push({ x: e.x, y: e.y, kind: "death", born: now });
        break;
      case "levelup":
        this.sparks.push({ x: e.x, y: e.y, kind: "levelup", born: now });
        break;
      case "beam":
        // Somebody going home, or arriving there: a shaft of light on the
        // tile they left and on the tile they landed on. The same drawing
        // does both ends, because it is the same thing happening twice —
        // the light takes you and the light sets you down — and a watcher
        // at either end has exactly one fact to read off it, which is that
        // a person was there or is there now.
        this.sparks.push({ x: e.x, y: e.y, kind: "beam", born: now });
        break;
      case "splash":
        // A line going into the water. One per cast, wherever the server
        // put it: somewhere in reach on an ordinary cast, and on the tile
        // the fish came off on a catch. The client is told the tile and
        // nothing else on purpose — which of the two it is, is exactly
        // what the player is meant to work out by watching, and a flag
        // saying "this one was a catch" would let the drawing give away
        // what the water is not supposed to.
        this.sparks.push({ x: e.x, y: e.y, kind: "splash", born: now });
        break;
      case "cleanse":
        // An antidote going off: the poison drawn up and out of whoever
        // is standing on the tile. One per cast, on the caster's tile,
        // for everyone who could see it, and sent whether or not there
        // was any poison to draw — what it marks is that the spell went
        // off, which the words said out loud a moment before already.
        this.sparks.push({ x: e.x, y: e.y, kind: "cleanse", born: now });
        break;
      case "mend":
        // A heal going off (2026-09-27: "add a healing spell"): sent on
        // every cast, whether or not there was a wound to close, beside
        // the green number that says how much was.
        this.sparks.push({ x: e.x, y: e.y, kind: "mend", born: now });
        break;
      case "nova":
        // A fire nova going off. ONE message on the caster's tile, with
        // how far the fire went in `value` — unlike a wave of dragon fire
        // or a demon's rift, which arrive a tile at a time. The server's
        // note on fireNova gives the reason and it is not about saving
        // bytes: a wave is chewed up by walls and a rift is a disc with
        // bites out of it, so the only honest way to say what either
        // covered is to list the tiles; a nova is a square round a tile
        // the client can already see, and the one fact missing is its
        // size.
        //
        // The reach is taken from the message and never assumed. See the
        // note on Spark.r.
        this.sparks.push({ x: e.x, y: e.y, kind: "nova", born: now, r: e.value ?? 0 });
        break;
      case "provoke":
      case "elbereth":
      case "convince":
      case "challenge":
      case "haste":
      case "light":
      case "unbond":
        // The rest of the spells going off, one per cast on the caster's
        // tile (asked for on 2026-09-24: "make sure all spells have a
        // graphical effect when cast"). Each carries nothing but its tile,
        // and all a watcher reads off it is which spell was just said;
        // what the spell then does is drawn from the state, if at all.
        this.sparks.push({ x: e.x, y: e.y, kind: e.kind, born: now });
        break;
      case "find":
        // A find person. The way to point comes in `value` on the caster's
        // screen and as nought on everybody else's: see Spark.dir.
        this.sparks.push({ x: e.x, y: e.y, kind: "find", born: now, dir: e.value ?? 0 });
        break;
      case "bond": {
        // A blood bond laid: a thread from creature e.id, the caster, to
        // the tile e.x, e.y the person taken on is standing on. Both ends
        // are pinned now, for the arc's reason.
        const src = e.id ? this.creatures.get(e.id) : undefined;
        this.sparks.push({ x: e.x, y: e.y, kind: "bond", born: now, fx: src?.x, fy: src?.y });
        break;
      }
    }
    // Whatever the effect put up is stamped with the level it happened
    // on, for the oblique view (2026-09-27, "implement the oblique view
    // with south and east faces visible") to draw it at that height: a
    // dragon's wave on a ledge burns on the ledge, not on the ground a
    // storey under it. Stamped here, once, rather than written into every
    // case above, because every case above is about what an effect looks
    // like and the level is not; a number merged into one already in the
    // air keeps the level it had.
    const z = e.z ?? 0;
    for (const [list, from] of [[this.floats, floats], [this.sparks, sparks], [this.shots, shots], [this.arcs, arcs]] as const) {
      for (let i = from; i < list.length; i++) list[i].z = z;
    }
    for (let i = floats; i < this.floats.length; i++) {
      const f = this.floats[i];
      f.on = this.standingOn(f.x, f.y, z);
    }
  }

  /** Drop expired transient visuals. */
  prune(now: number) {
    this.floats = this.floats.filter((f) => now - f.born < f.ttl);
    this.bubbles = this.bubbles.filter((b) => now - b.born < b.ttl);
    this.sparks = this.sparks.filter((s) => now - s.born < sparkMs(s));
    this.shots = this.shots.filter((s) => now - s.born < (s.ms ?? shotMs(s.kind)));
    this.arcs = this.arcs.filter((a) => now - a.born < (a.shaft ? SHAFT_MS : ARC_MS));
  }
}

/** The finished quests on NPCs' menus, as the server sends them, by kind. */
function topicsDoneOf(done: Record<string, number[]> | undefined): Map<string, Set<number>> {
  return new Map(Object.entries(done ?? {}).map(([kind, topics]) => [kind, new Set(topics)]));
}
